import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, TextField, Tooltip, Typography, useMediaQuery
} from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { motion } from 'motion/react';
import {
  applyNodeChanges, Background, BackgroundVariant, BaseEdge, Controls, EdgeLabelRenderer, getBezierPath, MarkerType,
  Position,
  MiniMap, Panel, ReactFlow, useEdgesState, useInternalNode, useNodesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { fetchProjectDagLayout, updateProjectDagLayout } from '../../Utils/actionsApi';
import { decisionPaths } from '../../Utils/actionFlowDecision';
import {
  buildDagNodeSearchIndex,
  findDagNodeSearchMatchKeysInIndex,
} from '../../Utils/dagSearch';
import {
  DRAFT_TUTORIAL_STORAGE_KEY,
  DRAFT_TUTORIAL_RESET_EVENT,
  dismissDraftTutorial as dismissDraftTutorialPreference,
  isDraftTutorialDismissed,
} from '../../Utils/onboardingPreferences';
import {
  DAG_HOVER_SHADOW,
  DAG_HOVER_TRANSITION,
  DAG_REDUCED_EFFECT_STYLES,
  DAG_REDUCED_MOTION_STYLES,
} from '../../Utils/dagInteractionStyles';
import { areDagRecordsEqual, createDagFrameScheduler, createDagNodeInternalsBatcher, flowLinkPositionSignature, reuseDagElements,
  shouldReduceDagEffects } from '../../Utils/dagPerformance';
import { createRafResizeObserver } from '../../Utils/resizeObserver';
import {
  getFlowTriggerConditionSummary,
  getFlowTriggerModeLabel,
  normalizeFlowTriggerMode,
} from '../../Utils/workflowTriggerVisuals';
import UnfoldingNode, {
  DagBuildFocusContext, DagEffectsContext, DagEntranceContext, DagInteractionContext, DagNodeInternalsContext, EditableDagNodeLabel
} from './UnfoldingNode';
import ActionFlowLogicalGroupFrames, { CollapsedLogicalGroupAnchor } from './ActionFlowLogicalGroupFrames';
import { buildActionLogicalGroupBounds, buildCollapsedLogicalGroupView, buildLogicalGroupTokenSummaries,
  moveActionLogicalGroup } from './actionFlowLogicalGroups';
import useCollapsedLogicalGroups from './useCollapsedLogicalGroups';
import DagHelp from './DagHelp';
import ProjectDagSections from './ProjectDagSections';
import { PROJECT_WORKSPACE_NODE_HEIGHT, PROJECT_WORKSPACE_NODE_WIDTH } from './projectDagNodeDimensions';
import {
  alignProjectDagFlowRows, constrainProjectDagDropNode, constrainProjectDagSections, DAG_FLOW_ANCHOR_Y, DAG_FLOW_LABEL_WIDTH,
  DAG_SECTION_PADDING, getProjectDagActionsStart,
  isProjectDagFlowLabelBelow,
} from './projectDagSectionsLayout';
import { buildConnectorPreparationNodes, preparationNodeKey } from './connectorPreparationNodes';
import { placeNewProjectDagNodes } from './projectDagNewNodePlacement';

const NODE_WIDTH = 154;
const NODE_HEIGHT = 150;
const HORIZONTAL_GAP = 220;
const VERTICAL_GAP = 190;
const BRANCH_PADDING = 80;
const MAX_ANIMATED_EDGES = 24;
const MAX_ANIMATED_NODES = 40;
const LARGE_DAG_NODE_THRESHOLD = 80;
const EMPTY_NODE_ACTIVITY_SEQUENCE = {};
const STORAGE_VERSION = 'v3';
const PROJECT_DAG_NODE_ID_PATTERN = /^(?:center|input:[a-z0-9]+:(?:\d+|default)|preparation:input:[a-z0-9]+:(?:\d+|default):(?:step-[A-Za-z0-9_-]{1,64}|native-[a-z0-9-]{1,64})|reanalysis:project|flow:\d+|action:\d+|add:\d+)$/;
const PROJECT_DAG_HANDLE_POSITIONS = new Set([
  Position.Left, Position.Right, Position.Top, Position.Bottom,
]);
const EDGE_COLORS = {
  pending: '#8a8a96',
  configurationError: '#d14343',
  mixed: '#d6a328',
  success: '#36a269',
  active: '#5757b0',
  activeDark: '#45439a',
  activeLight: '#a7a7ed',
  dropTarget: '#f59e0b',
  dropTargetLight: '#fde68a',
};

const nodeTypes = { unfolding: UnfoldingNode, logicalGroupAnchor: CollapsedLogicalGroupAnchor };

export const buildFlowExecutionStages = (nodes = []) => {
  const stages = [];
  nodes.forEach((node) => {
    const executionGroup = String(node?.actionMeta?.executionGroup || '').trim();
    const isParallelAction = (
      node?.nodeType === 'action'
      && node?.actionMeta?.groupMode === 'parallel'
      && executionGroup
    );
    const previousStage = stages[stages.length - 1];
    if (
      isParallelAction
      && previousStage?.executionMode === 'parallel'
      && previousStage.executionGroup === executionGroup
    ) {
      previousStage.nodes.push(node);
      return;
    }
    stages.push({
      executionMode: isParallelAction ? 'parallel' : 'sequential',
      executionGroup: isParallelAction ? executionGroup : null,
      nodes: [node],
    });
  });
  return stages;
};

export const buildFlowStageConnections = (branch) => {
  const stages = buildFlowExecutionStages(branch?.nodes || []);
  return stages.flatMap((stage, stageIndex) => {
    const sources = stageIndex === 0
      ? [{ key: 'center' }]
      : stages[stageIndex - 1].nodes;
    return stage.nodes.flatMap((target) => sources.map((source) => ({
      source: source.key,
      target: target.key,
    })));
  });
};

export const buildProjectDagInputConnections = (inputNodes = []) => (
  inputNodes.flatMap((node) => {
    const chain = [node.key, ...(node.preparationSteps || []).map((step) => preparationNodeKey(node, step)), 'center'];
    return chain.slice(0, -1).map((source, index) => ({ source, target: chain[index + 1], node }));
  })
);

const parseActionDataValue = (rawData) => {
  if (typeof rawData === 'string') {
    try {
      const parsed = JSON.parse(rawData);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_error) {
      return {};
    }
  }
  return rawData && typeof rawData === 'object' && !Array.isArray(rawData) ? rawData : {};
};

const parseActionData = (action) => ({
  ...parseActionDataValue(action?.data),
  ...parseActionDataValue(action?.customData),
});

const parsePositiveFlowId = (value) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

const FLOW_LINK_ACTION_TYPES = new Set(['condition', 'humaninloop', 'triggerflow', 'looptriggerflow']);

export const buildFlowActionConnections = (
  flowBranches = [],
  t = (_key, fallback) => fallback,
  positions = {}
) => {
  const flowsById = new Map(flowBranches.map((branch) => [
    String(branch?.flow?.id ?? ''),
    branch?.flow,
  ]));
  const outcomes = [
    { key: 'approved', labelKey: 'dag.human_in_loop_approved', fallback: 'Approved' },
    { key: 'rejected', labelKey: 'dag.human_in_loop_rejected', fallback: 'Rejected' },
  ];

  const choiceConnections = flowBranches.flatMap((branch) => (branch?.nodes || [])
    .filter((node) => {
      const actionType = String(node?.item?.type || '').trim().toLowerCase();
      return node?.nodeType === 'action' && FLOW_LINK_ACTION_TYPES.has(actionType)
        && (parsePositiveFlowId(node?.item?.id) || node.assistantPreview);
    })
    .flatMap((node) => {
      const actionType = String(node.item.type).trim().toLowerCase();
      const decision = actionType === 'condition';
      const flowCall = actionType === 'triggerflow' || actionType === 'looptriggerflow';
      const actionData = parseActionData(node.item);
      const configuredFlowIds = {
        approved: decision ? actionData.targetFlowId : parsePositiveFlowId(actionData.targetFlowId ?? actionData.target_flow_id),
        rejected: decision ? actionData.rejectFlowId : parsePositiveFlowId(actionData.rejectFlowId ?? actionData.reject_flow_id),
      };
      const sourceFlowId = branch.flow?.id;
      const routes = decision ? decisionPaths(actionData).map((path) => ({
        key: path.rule || path.outcome === 'success' ? 'approved' : 'rejected', label: path.label, targetFlowId: path.targetFlowId,
      })) : flowCall ? [{
        key: 'approved',
        label: actionType === 'looptriggerflow'
          ? t('dag.flow_repeat_link', 'For each item')
          : t('dag.flow_trigger_link', 'Run flow'),
        targetFlowId: configuredFlowIds.approved,
      }] : outcomes.map((outcome) => ({ ...outcome, label: t(outcome.labelKey, outcome.fallback),
        targetFlowId: configuredFlowIds[outcome.key] }));
      const grouped = new Map();
      return routes.flatMap(({ key, label, targetFlowId }) => {
        const targetFlow = targetFlowId ? flowsById.get(String(targetFlowId)) : null;
        if (!targetFlow || String(targetFlowId) === String(sourceFlowId)) return [];
        const targetKey = `flow:${targetFlow.id}`;
        if (decision && grouped.has(targetKey)) {
          grouped.get(targetKey).outcomeLabel += ` · ${label}`;
          return [];
        }
        const targetSide = getHumanOutcomeTargetSide(
          positions[node.key],
          positions[targetKey]
        );
        const connection = {
          source: node.key,
          sourceHandle: flowCall ? 'flow-call-source'
            : `branch-source-${getBranchSourceSide(positions[node.key], positions[targetKey])}`,
          target: targetKey,
          targetHandle: `human-outcome-${key}-target-${targetSide}`,
          outcome: flowCall ? 'flow-call' : key,
          outcomeLabel: label,
          outcomeBranch: true,
        };
        grouped.set(targetKey, connection);
        return [connection];
      });
    }));
  const sharedConnections = flowBranches.flatMap((branch) => {
    const targetId = branch.flow?.completion_flow_id;
    if (!targetId || !flowsById.has(String(targetId))) return [];
    let lastAction;
    for (let index = (branch.nodes?.length || 0) - 1; index >= 0; index -= 1) {
      if (branch.nodes[index].nodeType === 'action') { lastAction = branch.nodes[index]; break; }
    }
    if (!lastAction || String(branch.flow.id) === String(targetId)) return [];
    const target = `flow:${targetId}`;
    const side = getHumanOutcomeTargetSide(positions[lastAction.key], positions[target]);
    return [{ source: lastAction.key, target, targetHandle: `human-outcome-approved-target-${side}`,
      outcome: 'shared', outcomeLabel: t('workflow.condition.shared_label'), outcomeBranch: true }];
  });
  return [...choiceConnections, ...sharedConnections];
};

const getFlowBranchKey = (branch) => {
  const flowId = branch?.flow?.id
    ?? branch?.nodes?.find((node) => node?.nodeType === 'flow')?.id;
  return flowId === undefined || flowId === null ? null : String(flowId);
};

const getDagNodeFlowId = (node) => (
  node?.nodeType === 'flow'
    ? node?.item?.id
    : node?.actionMeta?.currentFlowId ?? node?.item?.flow_id ?? node?.item?.flowId ?? null
);

const EDGE_DROP_TOLERANCE_PX = 4;

const HANDLE_GEOMETRY = {
  preparation: { centerX: 77, centerY: 65, radiusX: 40, radiusY: 40, shape: 'ellipse' },
  input: { centerX: 77, centerY: 65, radiusX: 40, radiusY: 40, shape: 'ellipse' },
  action: { centerX: 77, centerY: 65, radiusX: 40, radiusY: 40, shape: 'ellipse' },
  output: { centerX: 77, centerY: 26, radiusX: 34, radiusY: 26, shape: 'rectangle' },
  center: { centerX: PROJECT_WORKSPACE_NODE_WIDTH / 2, centerY: PROJECT_WORKSPACE_NODE_HEIGHT / 2,
    radiusX: PROJECT_WORKSPACE_NODE_WIDTH / 2, radiusY: PROJECT_WORKSPACE_NODE_HEIGHT / 2, shape: 'rectangle' },
};

const getHandleGeometry = (node) => node?.kind === 'logicalGroup'
  ? { centerX: node.width / 2, centerY: node.height / 2,
    radiusX: node.width / 2, radiusY: node.height / 2, shape: 'rectangle' }
  : HANDLE_GEOMETRY[node?.kind];

const getProjectDagNodeRect = (reactFlowNode) => {
  const position = reactFlowNode?.positionAbsolute || reactFlowNode?.position;
  const width = Number(reactFlowNode?.measured?.width ?? reactFlowNode?.width ?? NODE_WIDTH);
  const height = Number(reactFlowNode?.measured?.height ?? reactFlowNode?.height ?? NODE_HEIGHT);
  if (
    !position
    || !Number.isFinite(Number(position.x))
    || !Number.isFinite(Number(position.y))
    || !Number.isFinite(width)
    || !Number.isFinite(height)
    || width <= 0
    || height <= 0
  ) {
    return null;
  }
  return {
    x: Number(position.x),
    y: Number(position.y),
    width,
    height,
  };
};

const getProjectDagHandlePoint = (reactFlowNode, otherReactFlowNode, handleType) => {
  const rect = getProjectDagNodeRect(reactFlowNode);
  if (!rect) return null;
  const dagNode = reactFlowNode?.data?.node || reactFlowNode;
  const anchor = dagNode?.handleAnchors?.[handleType];
  if (Number.isFinite(Number(anchor?.x)) && Number.isFinite(Number(anchor?.y))) {
    return {
      point: { x: rect.x + Number(anchor.x), y: rect.y + Number(anchor.y) },
      position: anchor.position,
    };
  }

  const otherRect = getProjectDagNodeRect(otherReactFlowNode);
  if (!otherRect) return null;
  const center = { x: rect.x + (rect.width / 2), y: rect.y + (rect.height / 2) };
  const otherCenter = {
    x: otherRect.x + (otherRect.width / 2),
    y: otherRect.y + (otherRect.height / 2),
  };
  if (Math.abs(otherCenter.x - center.x) >= Math.abs(otherCenter.y - center.y)) {
    const toRight = otherCenter.x >= center.x;
    return {
      point: { x: toRight ? rect.x + rect.width : rect.x, y: center.y },
      position: toRight ? Position.Right : Position.Left,
    };
  }
  const below = otherCenter.y >= center.y;
  return {
    point: { x: center.x, y: below ? rect.y + rect.height : rect.y },
    position: below ? Position.Bottom : Position.Top,
  };
};

const getProjectDagVisibleGeometry = (node) => {
  const rect = getProjectDagNodeRect(node);
  if (!rect) return null;
  const spec = node?.data?.node || node;
  const kind = spec.kind || (spec.nodeType === 'flow' ? 'output' : spec.nodeType);
  const geometry = HANDLE_GEOMETRY[kind];
  return geometry ? { ...geometry, centerX: rect.x + geometry.centerX, centerY: rect.y + geometry.centerY } : null;
};

const visibleProjectDagNodesTouch = (left, right) => {
  if (left?.hidden || right?.hidden) return false;
  const source = getProjectDagVisibleGeometry(left);
  const target = getProjectDagVisibleGeometry(right);
  // Metadata-only callers have no rectangles; the live canvas supplies both.
  if (!source || !target) return true;
  if (target.shape === 'rectangle') {
    const x = Math.max(target.centerX - target.radiusX, Math.min(source.centerX, target.centerX + target.radiusX));
    const y = Math.max(target.centerY - target.radiusY, Math.min(source.centerY, target.centerY + target.radiusY));
    return Math.hypot((source.centerX - x) / source.radiusX, (source.centerY - y) / source.radiusY) <= 1;
  }
  return Math.hypot((source.centerX - target.centerX) / (source.radiusX + target.radiusX),
    (source.centerY - target.centerY) / (source.radiusY + target.radiusY)) <= 1;
};

const getProjectDagBezierControls = (source, target) => {
  // Use the exact cubic path produced by the renderer, including reverse bends.
  const [path] = getBezierPath({ sourceX: source.point.x, sourceY: source.point.y,
    targetX: target.point.x, targetY: target.point.y, sourcePosition: source.position, targetPosition: target.position });
  const values = path.match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi)?.map(Number);
  return values?.length === 8 ? [{ x: values[2], y: values[3] }, { x: values[4], y: values[5] }] : null;
};

const getProjectDagDistanceToSegment = (point, start, end) => {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = (deltaX ** 2) + (deltaY ** 2);
  if (lengthSquared <= 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const projection = Math.max(0, Math.min(1, (
    ((point.x - start.x) * deltaX) + ((point.y - start.y) * deltaY)
  ) / lengthSquared));
  return Math.hypot(
    point.x - (start.x + (projection * deltaX)),
    point.y - (start.y + (projection * deltaY))
  );
};

const getProjectDagDistanceToEdge = (
  point, sourcePoint, targetPoint, sourceControl, targetControl
) => {
  let previousPoint = sourcePoint.point;
  let minimumDistance = Number.POSITIVE_INFINITY;
  for (let index = 1; index <= 32; index += 1) {
    const t = index / 32;
    const inverseT = 1 - t;
    const nextPoint = {
      x: (inverseT ** 3 * sourcePoint.point.x)
        + (3 * inverseT ** 2 * t * sourceControl.x)
        + (3 * inverseT * t ** 2 * targetControl.x)
        + (t ** 3 * targetPoint.point.x),
      y: (inverseT ** 3 * sourcePoint.point.y)
        + (3 * inverseT ** 2 * t * sourceControl.y)
        + (3 * inverseT * t ** 2 * targetControl.y)
        + (t ** 3 * targetPoint.point.y),
    };
    minimumDistance = Math.min(
      minimumDistance,
      getProjectDagDistanceToSegment(point, previousPoint, nextPoint)
    );
    previousPoint = nextPoint;
  }
  return minimumDistance;
};

export const getProjectDagActionDropTarget = (draggedNode, intersectingNodes = []) => {
  const draggedDagNode = draggedNode?.data?.node || draggedNode;
  if (draggedDagNode?.nodeType !== 'action') return null;

  const sourceFlowId = getDagNodeFlowId(draggedDagNode);
  if (sourceFlowId === undefined || sourceFlowId === null) return null;

  const targetCandidates = intersectingNodes
    .filter((candidate) => candidate?.id !== draggedNode?.id && visibleProjectDagNodesTouch(draggedNode, candidate))
    .map((candidate) => candidate?.data?.node || candidate)
    .filter((dagNode) => {
      if (!['flow', 'action'].includes(dagNode?.nodeType)) return false;
      const targetFlowId = getDagNodeFlowId(dagNode);
      return targetFlowId !== undefined
        && targetFlowId !== null;
    });
  const target = targetCandidates.find((dagNode) => (
    String(getDagNodeFlowId(dagNode)) !== String(sourceFlowId)
  )) || targetCandidates[0];

  if (!target) return null;
  return {
    actionNode: draggedDagNode,
    targetNode: target,
    sourceFlowId,
    targetFlowId: getDagNodeFlowId(target),
  };
};

const getProjectDagEdgeDropTarget = (
  draggedNode,
  reactFlowNodes = [],
  connections = [],
  acceptsTarget = () => false,
  { zoom = 1 } = {},
) => {
  const draggedDagNode = draggedNode?.data?.node || draggedNode;
  const draggedRect = getProjectDagNodeRect(draggedNode);
  if (!draggedRect) return null;

  const nodesByKey = new Map(reactFlowNodes.map((node) => [
    node?.id,
    node?.data?.node || node,
  ]));
  const reactFlowNodesByKey = new Map(reactFlowNodes.map((node) => [node?.id, node]));
  const icon = getProjectDagVisibleGeometry(draggedNode);
  if (!icon) return null;
  const dropPoint = { x: icon.centerX, y: icon.centerY };
  const tolerance = EDGE_DROP_TOLERANCE_PX / (Number.isFinite(zoom) && zoom > 0 ? zoom : 1);
  const dropDistance = Math.max(icon.radiusX, icon.radiusY) + tolerance;
  let closestCandidate = null;
  connections.forEach((connection) => {
    const sourceReactFlowNode = reactFlowNodesByKey.get(connection?.source);
    const targetReactFlowNode = reactFlowNodesByKey.get(connection?.target);
    const targetNode = nodesByKey.get(connection?.target);
    if (
      !sourceReactFlowNode
      || !targetReactFlowNode
      || !targetNode
      || connection?.outcomeBranch === true
      || connection?.hidden || connection?.data?.hideConnection
      || sourceReactFlowNode.hidden || targetReactFlowNode.hidden
      || (connection.source === 'center' && targetNode.nodeType === 'flow'
        && normalizeFlowTriggerMode(targetNode.item?.trigger_mode, targetNode.item?.auto_run_enabled) === 'disabled')
      || connection.source === draggedNode?.id
      || connection.target === draggedNode?.id
    ) return;
    if (!acceptsTarget(targetNode, nodesByKey.get(connection.source), connection)) return;
    const sourcePoint = getProjectDagHandlePoint(
      sourceReactFlowNode, targetReactFlowNode, 'source'
    );
    const targetPoint = getProjectDagHandlePoint(
      targetReactFlowNode, sourceReactFlowNode, 'target'
    );
    if (!sourcePoint || !targetPoint) return;
    const controls = getProjectDagBezierControls(sourcePoint, targetPoint);
    if (!controls) return;
    const [sourceControl, targetControl] = controls;
    const minX = Math.min(sourcePoint.point.x, sourceControl.x, targetControl.x, targetPoint.point.x);
    const maxX = Math.max(sourcePoint.point.x, sourceControl.x, targetControl.x, targetPoint.point.x);
    const minY = Math.min(sourcePoint.point.y, sourceControl.y, targetControl.y, targetPoint.point.y);
    const maxY = Math.max(sourcePoint.point.y, sourceControl.y, targetControl.y, targetPoint.point.y);
    if (
      dropPoint.x < minX - dropDistance
      || dropPoint.x > maxX + dropDistance
      || dropPoint.y < minY - dropDistance
      || dropPoint.y > maxY + dropDistance
    ) return;
    const distance = getProjectDagDistanceToEdge(
      dropPoint, sourcePoint, targetPoint, sourceControl, targetControl
    );
    if (distance > dropDistance || distance >= (closestCandidate?.distance ?? Infinity)) return;
    closestCandidate = {
      distance,
      actionNode: draggedDagNode,
      targetNode,
      targetEdgeId: `${connection.source}:${connection.target}`,
    };
  });

  const bestCandidate = closestCandidate;
  if (!bestCandidate) return null;
  return {
    actionNode: bestCandidate.actionNode,
    targetNode: bestCandidate.targetNode,
    targetEdgeId: bestCandidate.targetEdgeId,
  };
};

export const getProjectDagActionEdgeDropTarget = (draggedNode, reactFlowNodes = [], connections = [], options = {}) => {
  const dragged = draggedNode?.data?.node || draggedNode;
  const sourceFlowId = getDagNodeFlowId(dragged);
  if (dragged?.nodeType !== 'action' || sourceFlowId == null) return null;
  const target = getProjectDagEdgeDropTarget(draggedNode, reactFlowNodes, connections, (candidate) => (
    ['flow', 'action'].includes(candidate.nodeType) && getDagNodeFlowId(candidate) != null
  ), options);
  return target ? { ...target, sourceFlowId, targetFlowId: getDagNodeFlowId(target.targetNode) } : null;
};

export const getProjectDagPreparationDropTarget = (draggedNode, reactFlowNodes = [], connections = [], intersectingNodes = [], options = {}) => {
  const dragged = draggedNode?.data?.node || draggedNode;
  const step = dragged?.item?.preparationStep;
  if (dragged?.kind !== 'preparation' || !dragged.sourceNodeKey || step?.native
      || !step?.stepId || !Number.isInteger(step.editableIndex) || !Number.isInteger(step.editableCount)
      || step.editableIndex < 0 || step.editableIndex >= step.editableCount || step.editableCount < 2) return null;
  const draggedRect = getProjectDagNodeRect(draggedNode);
  if (!draggedRect) return null;
  const sameSource = (candidate) => candidate?.kind === 'preparation' && candidate.sourceNodeKey === dragged.sourceNodeKey;
  const target = getProjectDagEdgeDropTarget(draggedNode, reactFlowNodes, connections, (candidate, source) => (
    (sameSource(source) || source?.key === dragged.sourceNodeKey)
    && (candidate.key === 'center' || (sameSource(candidate) && !candidate.item?.preparationStep?.native))
  ), options);
  const nodeTarget = !target && intersectingNodes
    .filter((node) => node.id !== draggedNode.id && visibleProjectDagNodesTouch(draggedNode, node) && sameSource(node.data?.node || node)
      && !(node.data?.node || node).item?.preparationStep?.native)
    .map((node) => ({ node, rect: getProjectDagNodeRect(node) }))
    .filter(({ rect }) => rect)
    .sort((left, right) => Math.abs(left.rect.x - draggedRect.x) - Math.abs(right.rect.x - draggedRect.x))[0];
  if (!target && !nodeTarget) return null;
  const targetNode = target?.targetNode || nodeTarget.node.data?.node || nodeTarget.node;
  const insertBefore = Boolean(target) || draggedRect.x + draggedRect.width / 2 < nodeTarget.rect.x + nodeTarget.rect.width / 2;
  const insertion = targetNode.key === 'center' ? step.editableCount
    : targetNode.item?.preparationStep?.editableIndex + (insertBefore ? 0 : 1);
  const targetIndex = insertion - (step.editableIndex < insertion ? 1 : 0);
  if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= step.editableCount || targetIndex === step.editableIndex) return null;
  return { preparationNode: dragged, targetNode, targetEdgeId: target?.targetEdgeId,
    targetIndex, insertBefore };
};

export const isProjectDagDropTargetEdge = (source, target, targetNodeKey, targetEdgeId = null) => (
  targetEdgeId
    ? `${source}:${target}` === targetEdgeId
    : Boolean(targetNodeKey) && target === targetNodeKey
);

const getFlowActionKeys = (branch) => (
  (branch?.nodes || [])
    .filter((node) => node?.nodeType === 'action' && node?.key)
    .map((node) => String(node.key))
);

const getFlowActionStages = (branch) => buildFlowExecutionStages(
  (branch?.nodes || []).filter((node) => node?.nodeType === 'action' && node?.key)
);

const isFiniteDagPosition = (position) => (
  Number.isFinite(position?.x) && Number.isFinite(position?.y)
);

const getStageGeometry = (stage, positions) => {
  const actionPositions = stage.nodes
    .map((node) => positions[node.key])
    .filter(isFiniteDagPosition);
  if (actionPositions.length === 0) return null;
  return {
    x: actionPositions.reduce((total, position) => total + position.x, 0) / actionPositions.length,
    y: actionPositions.reduce((total, position) => total + position.y, 0) / actionPositions.length,
  };
};

const haveSameActionKeys = (leftKeys, rightKeys) => {
  if (leftKeys.length !== rightKeys.length) return false;
  const rightKeySet = new Set(rightKeys);
  return leftKeys.every((key) => rightKeySet.has(key));
};

const remapStructuralActionPositions = (positions, previousBranch, nextBranch) => {
  const previousKeys = new Set(getFlowActionKeys(previousBranch));
  const stages = getFlowActionStages(nextBranch);
  const result = { ...positions };
  let changed = false;
  let preceding = positions[`flow:${getFlowBranchKey(nextBranch)}`];
  stages.forEach((stage) => {
    const existing = stage.nodes.filter((node) => previousKeys.has(node.key));
    const geometry = getStageGeometry({ nodes: existing }, positions);
    const reference = preceding || geometry || stages.map((candidate) => getStageGeometry(candidate, positions)).find(Boolean);
    if (!reference) return;
    stage.nodes.forEach((node, index) => {
      if (previousKeys.has(node.key) || isFiniteDagPosition(result[node.key])) return;
      result[node.key] = {
        x: reference.x + HORIZONTAL_GAP,
        y: reference.y + index * VERTICAL_GAP,
      };
      changed = true;
    });
    preceding = geometry || getStageGeometry(stage, result) || preceding;
  });
  return changed ? result : positions;
};

export const remapProjectDagActionPositionsForOrder = (
  positions = {},
  previousBranches = [],
  nextBranches = []
) => {
  const previousBranchesByFlow = new Map(
    previousBranches
      .map((branch) => [getFlowBranchKey(branch), branch])
      .filter(([flowKey]) => flowKey !== null)
  );
  let remappedPositions = positions;

  nextBranches.forEach((branch) => {
    const flowKey = getFlowBranchKey(branch);
    const previousBranch = previousBranchesByFlow.get(flowKey);
    const previousActionKeys = previousBranch ? getFlowActionKeys(previousBranch) : null;
    const nextActionKeys = getFlowActionKeys(branch);
    if (!previousActionKeys || nextActionKeys.every((key, index) => key === previousActionKeys[index])) return;

    if (!haveSameActionKeys(previousActionKeys, nextActionKeys)) {
      remappedPositions = remapStructuralActionPositions(
        remappedPositions,
        previousBranch,
        branch
      );
      return;
    }

    const sourcePositions = remappedPositions;
    nextActionKeys.forEach((nextKey, index) => {
      const previousKey = previousActionKeys[index];
      if (!sourcePositions[previousKey]) return;
      if (remappedPositions === sourcePositions) remappedPositions = { ...sourcePositions };
      remappedPositions[nextKey] = { ...sourcePositions[previousKey] };
    });
  });

  return remappedPositions;
};

export const buildConnectorEdgeJournalNode = (node) => (
  node?.nodeType === 'reanalysis'
    ? {
      nodeType: 'reanalysis',
      nodeId: node?.id,
      label: node?.label,
      journalMode: 'node',
    }
    : {
      nodeType: 'connector',
      nodeId: node?.item?.datasource?.id ?? node?.id,
      label: node?.label,
      journalMode: 'connector-ingestion',
    }
);

export const resolveDagEdgeVisual = (runtime, error, { configurationBlocked = false } = {}) => {
  const inProgress = Math.max(Number(runtime?.inProgress || 0), 0);
  const completed = Math.max(Number(runtime?.completed || 0), 0);
  const failed = Math.max(Number(runtime?.failed || 0), 0);
  const runtimeStatus = String(runtime?.status || '').toLowerCase();
  const queued = runtimeStatus === 'queued' && inProgress <= 0;
  const active = ['running', 'processing'].includes(runtimeStatus) || inProgress > 0;

  if (configurationBlocked) {
    return {
      state: 'pending',
      color: EDGE_COLORS.pending,
      active: false,
      configurationBlocked: true,
    };
  }

  if (queued) {
    return {
      state: 'queued',
      color: EDGE_COLORS.active,
      active: true,
      queued: true,
    };
  }
  if (active) {
    return {
      state: 'active',
      color: EDGE_COLORS.active,
      active: true,
      queued: false,
    };
  }
  if (completed > 0 && failed > 0) {
    return { state: 'mixed', color: EDGE_COLORS.mixed, active: false };
  }
  if (error?.message || failed > 0 || runtimeStatus === 'failed') {
    return {
      state: 'error',
      color: EDGE_COLORS.configurationError,
      active: false,
    };
  }
  if (completed > 0 && ['completed', 'success'].includes(runtimeStatus)) {
    return { state: 'success', color: EDGE_COLORS.success, active: false };
  }
  return { state: 'pending', color: EDGE_COLORS.pending, active: false };
};

export const resolveDagEdgePresentation = (visual = {}) => {
  const state = String(visual?.state || 'pending').toLowerCase();
  const finalState = ['success', 'mixed', 'error'].includes(state);
  return {
    strokeWidth: finalState ? 3.6 : visual?.active ? 2.8 : 1.45,
    strokeDasharray: state === 'error' ? '9 7' : undefined,
    opacity: state === 'pending' ? 0.46 : 1,
    statusIcon: finalState ? state : null,
  };
};

const BASE_EDGE_STROKE_WIDTH = 1.45;
const BASE_EDGE_MARKER_SIZE = 18;

export const resolveDagEdgeMarkerSize = (strokeWidth) => {
  const normalizedStrokeWidth = Number(strokeWidth);
  if (!Number.isFinite(normalizedStrokeWidth) || normalizedStrokeWidth <= 0) {
    return BASE_EDGE_MARKER_SIZE;
  }
  return BASE_EDGE_MARKER_SIZE * (BASE_EDGE_STROKE_WIDTH / normalizedStrokeWidth);
};

const runtimeColor = (runtime, error) => resolveDagEdgeVisual(runtime, error).color;

const getMiniMapNodeColor = (node) => runtimeColor(node.data?.runtime, node.data?.error);

export const DagFlowEdgeLabel = ({
  value,
  onSave,
  ariaLabel,
  triggerLabel,
  triggerConditionSummary,
  onOpenFlow,
  flowKey,
  onHeightChange,
}) => {
  const labelRef = useRef(null);
  useEffect(() => {
    if (!flowKey || !onHeightChange || !labelRef.current) return undefined;
    const label = labelRef.current;
    const measure = () => {
      if (label.offsetHeight > 0) onHeightChange(flowKey, label.offsetHeight);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = createRafResizeObserver(measure);
    observer.observe(label);
    return () => observer.disconnect();
  }, [flowKey, onHeightChange]);
  const stopPointerDown = (event) => event.stopPropagation();
  const openFlow = (event) => {
    event.stopPropagation();
    onOpenFlow?.();
  };
  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget || !onOpenFlow || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    openFlow(event);
  };
  return (
    <Box
      ref={labelRef}
      className="nodrag nopan"
      data-testid="dag-flow-edge-label"
      onPointerDown={stopPointerDown}
      onClick={openFlow}
      onKeyDown={handleKeyDown}
      role={onOpenFlow ? 'button' : undefined}
      tabIndex={onOpenFlow ? 0 : undefined}
      aria-label={onOpenFlow ? `${value}: ${triggerLabel || ''}`.trim() : undefined}
      sx={{
        pointerEvents: 'all',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.2,
        width: DAG_FLOW_LABEL_WIDTH,
        cursor: onOpenFlow ? 'pointer' : 'default',
      }}
    >
      <EditableDagNodeLabel
        value={value}
        onSave={onSave}
        ariaLabel={ariaLabel}
        testId="dag-flow-edge-name"
        t={(_key, fallback) => fallback}
      />
      {triggerLabel && (
        <Typography
          data-testid="dag-flow-edge-trigger"
          variant="caption"
          sx={{
            color: '#69697d',
            fontSize: { xs: '9px', md: '10px' },
            fontWeight: 500,
            lineHeight: 1.2,
            textAlign: 'center',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            px: 0.5,
            py: 0.15,
            borderRadius: 0.75,
            backgroundColor: '#fbfbff',
          }}
        >
          {triggerLabel}
        </Typography>
      )}
      {triggerConditionSummary && (
        <Typography
          data-testid="dag-flow-edge-trigger-condition"
          variant="caption"
          sx={{
            color: '#777789',
            fontSize: { xs: '8px', md: '9px' },
            fontWeight: 500,
            lineHeight: 1.25,
            textAlign: 'center',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            px: 0.4,
          }}
        >
          {triggerConditionSummary}
        </Typography>
      )}
    </Box>
  );
};

const MINI_MAP_STYLE = {
  width: 150,
  height: 92,
  // Leave the right-hand column clear for the fixed upload and events buttons.
  marginRight: 96,
  border: '1px solid rgba(87,87,176,.18)',
  borderRadius: 10,
};

const FlowLabelAnchor = ({ flowKey, children }) => {
  const node = useInternalNode(flowKey);
  const center = useInternalNode('center');
  if (!node) return null;
  const { x, y } = node.internals.positionAbsolute;
  const sectionStart = center && getProjectDagActionsStart(
    center.internals.positionAbsolute.x, center.data?.node?.width || NODE_WIDTH
  );
  const belowFlow = isProjectDagFlowLabelBelow(x, sectionStart);
  return (
    <Box className="nodrag nopan" sx={{
      position: 'absolute',
      transform: belowFlow
        ? `translate(-50%, 0) translate(${x + NODE_WIDTH / 2}px, ${y + 100}px)`
        : `translate(-100%, -50%) translate(${x + 27}px, ${y + DAG_FLOW_ANCHOR_Y}px)`,
      pointerEvents: 'all',
    }}>
      {children}
    </Box>
  );
};

const UnfoldingEdge = ({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, data
}) => {
  const [path, labelX, labelY] = getBezierPath({
    sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition
  });
  const [packetBursts, setPacketBursts] = useState([]);
  const previousActivitySequenceRef = useRef(Number(data?.activitySequence) || 0);
  const packetBurstTimeoutsRef = useRef(new Set());
  useEffect(() => {
    const activitySequence = Math.max(Number(data?.activitySequence) || 0, 0);
    const previousActivitySequence = previousActivitySequenceRef.current;
    previousActivitySequenceRef.current = activitySequence;
    if (activitySequence <= previousActivitySequence || !data?.animatePackets) return;

    const burst = {
      id: activitySequence,
      count: Math.min(activitySequence - previousActivitySequence, 3),
    };
    setPacketBursts((current) => [...current, burst].slice(-3));
    const timeout = setTimeout(() => {
      setPacketBursts((current) => current.filter((item) => item.id !== burst.id));
      packetBurstTimeoutsRef.current.delete(timeout);
    }, 1_150);
    packetBurstTimeoutsRef.current.add(timeout);
  }, [data?.activitySequence, data?.animatePackets]);
  useEffect(() => () => {
    packetBurstTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    packetBurstTimeoutsRef.current.clear();
  }, []);
  const color = data?.color || '#7777c5';
  const isDropTarget = data?.dropTarget === true;
  const renderedColor = isDropTarget ? EDGE_COLORS.dropTarget : color;
  const gradientId = `dag-edge-gradient-${String(id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const animatedStroke = !isDropTarget && data?.active && data?.animateGradient
    ? `url(#${gradientId})`
    : renderedColor;
  const openJournal = (event) => {
    event.stopPropagation();
    data?.onOpenJournal?.(data.journalNode);
  };
  const openFlow = (event) => {
    event.stopPropagation();
    data?.onOpenFlow?.(data.flowNode);
  };
  const statusIcon = {
    success: <CheckCircleRoundedIcon sx={{ fontSize: 19 }} />,
    mixed: <WarningAmberRoundedIcon sx={{ fontSize: 19 }} />,
    error: <ErrorOutlineRoundedIcon sx={{ fontSize: 19 }} />,
  }[data?.statusIcon] || null;
  return (
    <>
      {!data?.hideConnection && <g
        data-dag-edge-id={id}
        data-dag-edge-state={data?.state || 'pending'}
        data-dag-edge-highlighted={isDropTarget ? 'true' : 'false'}
      >
        {data?.active && data?.animateGradient && (
          <defs>
            <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={sourceX}
              y1={sourceY}
              x2={targetX}
              y2={targetY}
            >
              <stop offset="0%" stopColor={EDGE_COLORS.activeDark} />
              <stop offset="0%" stopColor={EDGE_COLORS.activeDark}>
                <animate attributeName="offset" values="-0.35;1" dur="1.75s" repeatCount="indefinite" />
              </stop>
              <stop offset="0%" stopColor={EDGE_COLORS.activeLight}>
                <animate attributeName="offset" values="-0.18;1.17" dur="1.75s" repeatCount="indefinite" />
              </stop>
              <stop offset="0%" stopColor={EDGE_COLORS.activeDark}>
                <animate attributeName="offset" values="0;1.35" dur="1.75s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={EDGE_COLORS.activeDark} />
            </linearGradient>
          </defs>
        )}
        <BaseEdge
          id={id}
          path={path}
          markerEnd={markerEnd}
          style={{
            stroke: animatedStroke,
            strokeWidth: data?.strokeWidth,
            strokeDasharray: data?.strokeDasharray,
            opacity: isDropTarget ? 1 : data?.opacity,
            transition: 'stroke 220ms cubic-bezier(0.22, 1, 0.36, 1), stroke-width 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease',
          }}
        />
        {packetBursts.flatMap((burst) => (
          Array.from({ length: burst.count }, (_, packetIndex) => (
            <g
              key={"packet-" + burst.id + "-" + packetIndex}
              pointerEvents="none"
              style={{ filter: 'drop-shadow(0 0 5px rgba(167, 167, 237, 0.95))' }}
            >
              <circle r="6" fill={EDGE_COLORS.activeLight} opacity="0.5" />
              <circle r="3" fill="#ffffff" stroke={EDGE_COLORS.active} strokeWidth="1.5" />
              <animateMotion
                dur="0.78s"
                begin={(packetIndex * 0.11) + "s"}
                fill="freeze"
                path={path}
              />
            </g>
          ))
        ))}
        {isDropTarget && (
          <path
            d={path}
            fill="none"
            stroke={EDGE_COLORS.dropTargetLight}
            strokeWidth={Math.max(Number(data?.strokeWidth) || 0, 5.5)}
            strokeLinecap="round"
            pointerEvents="none"
            opacity={data?.animateDropTarget ? undefined : 0.9}
          >
            {data?.animateDropTarget && (
              <animate
                attributeName="stroke-opacity"
                values="0.25;0.95;0.25"
                dur="1.45s"
                repeatCount="indefinite"
              />
            )}
          </path>
        )}
        {data?.state === 'queued' && data?.animateQueuedPulse && (
          <path
            d={path}
            fill="none"
            stroke={EDGE_COLORS.activeLight}
            strokeWidth="3.4"
            strokeLinecap="round"
            pointerEvents="none"
          >
            <animate
              attributeName="stroke-opacity"
              values="0.18;0.78;0.18"
              dur="1.8s"
              repeatCount="indefinite"
            />
          </path>
        )}
        {data?.clickable && (
          <path
            d={path}
            fill="none"
            stroke="transparent"
            strokeWidth="18"
            pointerEvents="stroke"
            cursor="pointer"
            data-testid="dag-connector-edge-hitarea"
            aria-label={data?.journalLabel}
            onClick={openJournal}
          />
        )}
        {data?.flowClickable && (
          <path
            d={path}
            fill="none"
            stroke="transparent"
            strokeWidth="18"
            pointerEvents="stroke"
            cursor="pointer"
            data-testid="dag-flow-edge-hitarea"
            aria-label={data?.flowAriaLabel || data?.flowLabel}
            onClick={openFlow}
          />
        )}
      </g>}
      {data?.outcomeLabel && (
        <EdgeLabelRenderer>
          <Typography
            component="span"
            className="nodrag nopan"
            data-testid={`dag-human-outcome-edge-label-${String(id).replace(/[^a-zA-Z0-9_-]/g, '-')}`}
            aria-label={data.outcomeLabel}
            sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              color: renderedColor,
              fontSize: { xs: '9px', md: '10px' },
              fontWeight: 750,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              WebkitTextStroke: '3px #fff',
              paintOrder: 'stroke fill',
            }}
          >
            {data.outcomeLabel}
          </Typography>
        </EdgeLabelRenderer>
      )}
      {data?.clickable && statusIcon && (
        <EdgeLabelRenderer>
          <IconButton
            className="nodrag nopan"
            size="small"
            aria-label={data?.journalLabel}
            data-testid="dag-connector-edge-status"
            data-edge-id={id}
            data-edge-state={data?.state}
            onClick={openJournal}
            sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              width: 30,
              height: 30,
              pointerEvents: 'all',
              color: renderedColor,
              backgroundColor: '#fff',
              border: `2px solid ${renderedColor}`,
              boxShadow: '0 3px 10px rgba(37, 37, 70, .2)',
              transition: DAG_HOVER_TRANSITION,
              '&:hover': {
                backgroundColor: '#f8f8ff',
                boxShadow: DAG_HOVER_SHADOW,
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px) scale(1.04)`,
              },
            }}
          >
            <motion.span
              key={data?.statusIcon}
              initial={data?.reduceMotion ? false : { opacity: 0, scale: 0.55, rotate: -12 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 460, damping: 22 }}
              style={{ display: 'inline-flex' }}
            >
              {statusIcon}
            </motion.span>
          </IconButton>
        </EdgeLabelRenderer>
      )}
      {data?.flowLabel && (
        <EdgeLabelRenderer>
          <FlowLabelAnchor flowKey={data.flowNode?.key}>
            <DagFlowEdgeLabel
              value={data.flowLabel}
              onSave={data.onRenameFlow
                ? (name) => data.onRenameFlow(data.flowItem, name)
                : undefined}
              ariaLabel={data.flowLabelAriaLabel}
              triggerLabel={data.flowTriggerLabel}
              triggerConditionSummary={data.flowTriggerConditionSummary}
              onOpenFlow={data.onOpenFlow ? () => data.onOpenFlow(data.flowNode) : undefined}
              flowKey={data.flowNode?.key}
              onHeightChange={data.onFlowLabelHeightChange}
            />
          </FlowLabelAnchor>
        </EdgeLabelRenderer>
      )}
      {data?.canGroupWithPrevious && (
        <EdgeLabelRenderer>
          <Tooltip title={data.groupLabel} arrow>
            <IconButton
              className="nodrag nopan"
              size="small"
              aria-label={data.groupLabel}
              data-edge-id={id}
              onClick={(event) => {
                event.stopPropagation();
                data.onGroupWithPrevious?.(data.actionId);
              }}
              sx={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                width: 32,
                height: 32,
                pointerEvents: 'all',
                color: '#5757b0',
                backgroundColor: '#fff',
                border: '2px solid rgba(87,87,176,.42)',
                boxShadow: '0 3px 10px rgba(87,87,176,.18)',
                transition: DAG_HOVER_TRANSITION,
                '&:hover': {
                  backgroundColor: '#f1f0ff',
                  borderColor: '#5757b0',
                  boxShadow: DAG_HOVER_SHADOW,
                  transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px) scale(1.04)`,
                },
              }}
            >
              <CallSplitIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = { unfolding: React.memo(UnfoldingEdge) };



const DEFAULT_HANDLE_ANCHORS = {
  preparation: {
    target: { x: 37, y: 65, position: Position.Left },
    source: { x: 117, y: 65, position: Position.Right },
  },
  input: {
    source: { x: 117, y: 65, position: Position.Right },
  },
  action: {
    target: { x: 37, y: 65, position: Position.Left },
    source: { x: 117, y: 65, position: Position.Right },
  },
  output: {
    target: { x: 43, y: 26, position: Position.Left },
    source: { x: 111, y: 26, position: Position.Right },
  },
  center: {
    target: { x: 0, y: PROJECT_WORKSPACE_NODE_HEIGHT / 2, position: Position.Left },
    source: { x: PROJECT_WORKSPACE_NODE_WIDTH, y: PROJECT_WORKSPACE_NODE_HEIGHT / 2, position: Position.Right },
  },
};

const resolveAnchorSide = (normalizedX, normalizedY) => {
  if (Math.abs(normalizedX) >= Math.abs(normalizedY)) {
    return normalizedX >= 0 ? Position.Right : Position.Left;
  }
  return normalizedY >= 0 ? Position.Bottom : Position.Top;
};

const resolveBoundaryAnchor = (geometry, deltaX, deltaY) => {
  if (!geometry || geometry.shape === 'fixed' || (!deltaX && !deltaY)) return null;
  const normalizedX = deltaX / geometry.radiusX;
  const normalizedY = deltaY / geometry.radiusY;
  const denominator = geometry.shape === 'rectangle'
    ? Math.max(Math.abs(normalizedX), Math.abs(normalizedY))
    : Math.sqrt((normalizedX ** 2) + (normalizedY ** 2));
  if (!Number.isFinite(denominator) || denominator <= 0) return null;
  const scale = 1 / denominator;
  return {
    x: geometry.centerX + (deltaX * scale),
    y: geometry.centerY + (deltaY * scale),
    position: resolveAnchorSide(normalizedX, normalizedY),
  };
};

const getHumanOutcomeTargetSide = (sourcePosition, targetPosition) => {
  if (!sourcePosition || !targetPosition) return 'left';
  const sourceGeometry = HANDLE_GEOMETRY.action;
  const targetGeometry = HANDLE_GEOMETRY.output;
  const sourceCenter = {
    x: Number(sourcePosition.x || 0) + sourceGeometry.centerX,
    y: Number(sourcePosition.y || 0) + sourceGeometry.centerY,
  };
  const targetCenter = {
    x: Number(targetPosition.x || 0) + targetGeometry.centerX,
    y: Number(targetPosition.y || 0) + targetGeometry.centerY,
  };
  const targetAnchor = resolveBoundaryAnchor(
    targetGeometry,
    sourceCenter.x - targetCenter.x,
    sourceCenter.y - targetCenter.y
  );
  return ({
    [Position.Left]: 'left',
    [Position.Right]: 'right',
    [Position.Top]: 'top',
    [Position.Bottom]: 'bottom',
  })[targetAnchor?.position] || 'left';
};

const getBranchSourceSide = (sourcePosition, targetPosition) => {
  if (!sourcePosition || !targetPosition) return 'right';
  const source = HANDLE_GEOMETRY.action;
  const target = HANDLE_GEOMETRY.output;
  const anchor = resolveBoundaryAnchor(source,
    Number(targetPosition.x || 0) + target.centerX - Number(sourcePosition.x || 0) - source.centerX,
    Number(targetPosition.y || 0) + target.centerY - Number(sourcePosition.y || 0) - source.centerY);
  return ({ [Position.Left]: 'left', [Position.Right]: 'right',
    [Position.Top]: 'top', [Position.Bottom]: 'bottom' })[anchor?.position] || 'right';
};

export const buildDagHandleConnectionIndex = (connections = []) => {
  const incoming = new Map();
  const outgoing = new Map();
  const neighbors = new Map();
  const appendNeighbor = (index, key, neighbor) => {
    if (!index.has(key)) index.set(key, []);
    index.get(key).push(neighbor);
  };
  connections.forEach(({ source, target, sourceHandle, targetHandle }) => {
    if (!sourceHandle || sourceHandle === 'source') appendNeighbor(outgoing, source, target);
    if (!targetHandle || targetHandle === 'target') appendNeighbor(incoming, target, source);
    // A custom handle on one end can still affect the ordinary other end.
    appendNeighbor(neighbors, source, target);
    appendNeighbor(neighbors, target, source);
  });
  return { incoming, outgoing, neighbors };
};

export const calculateDynamicHandleAnchors = (
  nodeSpecs = [],
  positions = {},
  connections = [],
  connectionIndex = buildDagHandleConnectionIndex(connections),
  affectedNodeKeys = null
) => {
  const nodesByKey = new Map(nodeSpecs.map((node) => [node.key, node]));
  const { incoming, outgoing } = connectionIndex;
  const absoluteCenter = (nodeKey) => {
    const node = nodesByKey.get(nodeKey);
    const geometry = getHandleGeometry(node);
    const position = positions[nodeKey];
    if (!node || !geometry || !position) return null;
    return {
      x: Number(position.x || 0) + geometry.centerX,
      y: Number(position.y || 0) + geometry.centerY,
    };
  };
  const averageNeighborCenter = (neighborKeys) => {
    let count = 0;
    let x = 0;
    let y = 0;
    for (const key of neighborKeys || []) {
      const center = absoluteCenter(key);
      if (!center) continue;
      count += 1;
      x += center.x;
      y += center.y;
    }
    return count > 0 ? { x: x / count, y: y / count } : null;
  };

  const affectedNodes = affectedNodeKeys
    ? Array.from(affectedNodeKeys, (key) => nodesByKey.get(key)).filter(Boolean)
    : nodeSpecs;
  return Object.fromEntries(affectedNodes.map((node) => {
    const geometry = getHandleGeometry(node);
    const defaults = DEFAULT_HANDLE_ANCHORS[node.kind] || {};
    if (!geometry) return [node.key, defaults];
    const center = absoluteCenter(node.key);
    if (!center) return [node.key, defaults];
    const targetNeighbor = averageNeighborCenter(incoming.get(node.key));
    const sourceNeighbor = averageNeighborCenter(outgoing.get(node.key));
    return [node.key, {
      ...(defaults.target ? {
        target: targetNeighbor
          && node.kind !== 'center'
          ? resolveBoundaryAnchor(
            geometry,
            targetNeighbor.x - center.x,
            targetNeighbor.y - center.y
          ) || defaults.target
          : defaults.target,
      } : {}),
      ...(defaults.source ? {
        source: sourceNeighbor
          && node.kind !== 'center'
          ? resolveBoundaryAnchor(
            geometry,
            sourceNeighbor.x - center.x,
            sourceNeighbor.y - center.y
          ) || defaults.source
          : defaults.source,
      } : {}),
    }];
  }));
};

export const buildProjectDagLayoutSnapshot = (
  nodeSpecs = [],
  positions = {},
  connections = []
) => {
  const normalizedPositions = normalizeProjectDagNodePositions(positions);
  return {
    nodes: normalizedPositions,
    handles: normalizeProjectDagHandlePositions(
      calculateDynamicHandleAnchors(nodeSpecs, normalizedPositions, connections)
    ),
  };
};

// Compare normalized layouts independently of the server's JSON object key order.
// Otherwise an idle canvas can repeatedly save its stale geometry on runtime updates.
const buildProjectDagLayoutSignature = ({ nodes = {}, handles = {} }) => JSON.stringify({
  nodes: Object.keys(nodes).sort().map((nodeId) => [nodeId, nodes[nodeId]]),
  handles: Object.keys(handles).sort().map((nodeId) => [nodeId, handles[nodeId]]),
});

const sameAnchor = (current, next) => (
  (!current && !next)
  || (
    current
    && next
    && current.x === next.x
    && current.y === next.y
    && current.position === next.position
  )
);

export const applyDynamicHandleAnchors = (
  reactFlowNodes,
  connections,
  preferredAnchors = {},
  connectionIndex = buildDagHandleConnectionIndex(connections),
  changedNodeKeys = null
) => {
  const nodeSpecs = reactFlowNodes.map((node) => node.data.node || {
    key: node.id, kind: 'logicalGroup', width: node.width, height: node.height,
  });
  const positions = Object.fromEntries(
    reactFlowNodes.map((node) => [node.id, node.position])
  );
  const affectedNodeKeys = changedNodeKeys ? new Set(changedNodeKeys) : null;
  if (affectedNodeKeys) {
    changedNodeKeys.forEach((key) => {
      connectionIndex.neighbors.get(key)?.forEach((neighbor) => affectedNodeKeys.add(neighbor));
    });
  }
  const anchors = calculateDynamicHandleAnchors(
    nodeSpecs, positions, connections, connectionIndex, affectedNodeKeys
  );
  return reactFlowNodes.map((node) => {
    if (!node.data.node) return node;
    if (affectedNodeKeys && !affectedNodeKeys.has(node.id)) return node;
    const nextAnchors = preferredAnchors[node.id] || anchors[node.id];
    const currentAnchors = node.data.node.handleAnchors;
    if (
      sameAnchor(currentAnchors?.target, nextAnchors?.target)
      && sameAnchor(currentAnchors?.source, nextAnchors?.source)
    ) {
      return node;
    }
    return {
      ...node,
      data: {
        ...node.data,
        node: {
          ...node.data.node,
          handleAnchors: nextAnchors,
        },
      },
    };
  });
};

// Collapsing changes the visible endpoints, not the saved graph geometry.
// Recalculate only the exposed neighbors against the box instead of its hidden
// members; expanding can then reuse the original nodes and their anchors.
export const reconnectCollapsedGroupHandles = (graph) => {
  const anchors = new Set(graph.nodes.filter((node) => node.type === 'logicalGroupAnchor').map((node) => node.id));
  if (!anchors.size) return graph;
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const edges = graph.edges.map((edge) => {
    if (edge.hidden) return edge;
    if (anchors.has(edge.source) && edge.targetHandle?.startsWith('human-outcome-')) {
      return { ...edge, targetHandle: 'target' };
    }
    if (anchors.has(edge.target) && edge.sourceHandle?.startsWith('branch-source-')) {
      const source = byId.get(edge.source);
      const target = byId.get(edge.target);
      const geometry = getHandleGeometry(source?.data.node);
      if (!source || !target || !geometry) return edge;
      const anchor = resolveBoundaryAnchor(geometry,
        target.position.x + target.width / 2 - source.position.x - geometry.centerX,
        target.position.y + target.height / 2 - source.position.y - geometry.centerY);
      return anchor ? { ...edge, sourceHandle: `branch-source-${anchor.position}` } : edge;
    }
    return edge;
  });
  const connections = edges.filter((edge) => !edge.hidden);
  const affected = new Set();
  connections.forEach(({ source, target }) => {
    if (anchors.has(source) && !anchors.has(target)) affected.add(target);
    if (anchors.has(target) && !anchors.has(source)) affected.add(source);
  });
  const connectionHandles = new Map([...anchors].map((id) => [id, []]));
  const projectedEdges = edges.map((edge, index) => {
    if (edge.hidden) return edge;
    let projected = edge;
    ['source', 'target'].forEach((type) => {
      if (!anchors.has(edge[type])) return;
      const box = byId.get(edge[type]);
      const other = byId.get(edge[type === 'source' ? 'target' : 'source']);
      if (!other) return;
      const geometry = getHandleGeometry({ kind: 'logicalGroup', width: box.width, height: box.height });
      const otherGeometry = getHandleGeometry(other.data.node || {
        kind: 'logicalGroup', width: other.width, height: other.height,
      });
      if (!otherGeometry) return;
      const anchor = resolveBoundaryAnchor(geometry,
        other.position.x + otherGeometry.centerX - box.position.x - geometry.centerX,
        other.position.y + otherGeometry.centerY - box.position.y - geometry.centerY);
      if (!anchor) return;
      const id = `${type}:${edge.id || index}`;
      connectionHandles.get(box.id).push({ id, type, ...anchor });
      projected = { ...projected, [`${type}Handle`]: id };
    });
    return projected;
  });
  const projectedNodes = applyDynamicHandleAnchors(graph.nodes, connections, {},
    buildDagHandleConnectionIndex(connections), affected);
  return { edges: projectedEdges, nodes: projectedNodes.map((node) => anchors.has(node.id)
    ? { ...node, data: { ...node.data, connectionHandles: connectionHandles.get(node.id) } } : node) };
};

// Cache only geometry: runtime, labels, search and permissions still use fresh data.
export const createDagHandleAnchorUpdater = () => {
  let previousConnections = null;
  let previousGeometry = new Map();
  return (nextNodes, connections, preferredAnchors = {}, connectionIndex) => {
    const sameConnections = previousConnections === connections || (
      previousConnections?.length === connections.length
      && connections.every((connection, index) => (
        ['source', 'target', 'sourceHandle', 'targetHandle'].every((key) => (
          connection[key] === previousConnections[index][key]
        ))
      ))
    );
    const sameGeometry = previousGeometry.size === nextNodes.length
      && nextNodes.every((node) => {
        const previous = previousGeometry.get(node.id);
        const preferred = preferredAnchors[node.id];
        return previous
          && previous.kind === node.data.node.kind
          && previous.x === node.position.x
          && previous.y === node.position.y
          && Boolean(previous.preferred) === Boolean(preferred)
          && sameAnchor(previous.preferred?.target, preferred?.target)
          && sameAnchor(previous.preferred?.source, preferred?.source);
      });
    const anchoredNodes = sameConnections && sameGeometry
      ? nextNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          node: { ...node.data.node, handleAnchors: previousGeometry.get(node.id).anchors },
        },
      }))
      : applyDynamicHandleAnchors(nextNodes, connections, preferredAnchors, connectionIndex);
    previousConnections = connections;
    if (!sameConnections || !sameGeometry) {
      previousGeometry = new Map(anchoredNodes.map((node) => [node.id, {
        kind: node.data.node.kind,
        x: node.position.x,
        y: node.position.y,
        anchors: node.data.node.handleAnchors,
        preferred: preferredAnchors[node.id],
      }]));
    }
    return anchoredNodes;
  };
};

const aggregateRuntime = (runtimes) => {
  const values = runtimes.filter(Boolean);
  if (values.length === 0) return null;
  const processing = values.filter((runtime) => runtime.status === 'processing');
  const queued = values.filter((runtime) => runtime.status === 'queued');
  const failed = values.reduce((total, runtime) => total + Number(runtime.failed || 0), 0);
  const completed = values.reduce((total, runtime) => total + Number(runtime.completed || 0), 0);
  const skipped = values.reduce((total, runtime) => total + Number(runtime.skipped || 0), 0);
  return {
    status: processing.length > 0
      ? 'processing'
      : queued.length > 0
        ? 'queued'
        : failed > 0
          ? 'failed'
          : completed > 0
            ? 'completed'
            : skipped > 0
              ? 'skipped'
              : 'pending',
    inProgress: processing.reduce((total, runtime) => total + Number(runtime.inProgress || 1), 0),
    completed,
    failed,
    skipped,
  };
};

const verticalPositions = (count, graphHeight) => Array.from({ length: count }, (_unused, index) => {
  const totalHeight = Math.max(0, (count - 1) * VERTICAL_GAP);
  return Math.max(45, (graphHeight - NODE_HEIGHT - totalHeight) / 2 + index * VERTICAL_GAP);
});

export const normalizeProjectDagNodePositions = (positions) => Object.fromEntries(
  Object.entries(positions || {}).filter(([nodeId, position]) => (
    PROJECT_DAG_NODE_ID_PATTERN.test(nodeId)
    && Number.isFinite(position?.x)
    && Number.isFinite(position?.y)
  )).map(([nodeId, position]) => [
    nodeId,
    {
      x: Math.round(Number(position.x) * 100) / 100,
      y: Math.round(Number(position.y) * 100) / 100,
    },
  ])
);

export const normalizeProjectDagHandlePositions = (handles) => Object.fromEntries(
  Object.entries(handles || {}).filter(([nodeId, nodeHandles]) => (
    PROJECT_DAG_NODE_ID_PATTERN.test(nodeId)
    && nodeHandles
    && typeof nodeHandles === 'object'
  )).map(([nodeId, nodeHandles]) => [
    nodeId,
    Object.fromEntries(
      ['target', 'source'].filter((handleType) => {
        const anchor = nodeHandles[handleType];
        return (
          Number.isFinite(anchor?.x)
          && Number.isFinite(anchor?.y)
          && PROJECT_DAG_HANDLE_POSITIONS.has(anchor?.position)
        );
      }).map((handleType) => {
        const anchor = nodeHandles[handleType];
        return [handleType, {
          x: Math.round(Number(anchor.x) * 100) / 100,
          y: Math.round(Number(anchor.y) * 100) / 100,
          position: anchor.position,
        }];
      })
    ),
  ]).filter(([_nodeId, nodeHandles]) => Object.keys(nodeHandles).length > 0)
);

const normalizeConnectorType = (value) => (
  String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
);

export const buildProjectDagTemplateNodeKeys = (inputNodes = []) => {
  const inputsByType = new Map();
  inputNodes.forEach((node) => {
    const type = normalizeConnectorType(node?.item?.type || String(node?.key || '').split(':')[1]);
    if (!inputsByType.has(type)) inputsByType.set(type, []);
    inputsByType.get(type).push(node);
  });
  const keys = new Map();
  inputsByType.forEach((matchingInputs, type) => {
    matchingInputs.sort((left, right) => {
      const leftId = Number(
        left?.item?.datasource?.id ?? String(left?.key || '').split(':')[2]
      );
      const rightId = Number(
        right?.item?.datasource?.id ?? String(right?.key || '').split(':')[2]
      );
      if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId;
      return String(left?.key || '').localeCompare(String(right?.key || ''));
    }).forEach((node, ordinal) => keys.set(node.key, `input:${type}:${ordinal}`));
  });
  return keys;
};

export const getProjectDagTemplateNodeKey = (
  node, inputNodes = [], templateNodeKeys = buildProjectDagTemplateNodeKeys(inputNodes)
) => {
  if (node?.kind !== 'input') return null;
  const connectorType = normalizeConnectorType(
    node?.item?.type || String(node?.key || '').split(':')[1]
  );
  if (!connectorType) return null;
  return templateNodeKeys.get(node.key) || `input:${connectorType}:0`;
};

export const keepProjectDagStructuralElements = (changes = []) => (
  changes.filter((change) => change?.type !== 'remove')
);

export const areProjectDagNodesReadyForFit = (nodeSpecs = [], renderedNodes = []) => {
  if (nodeSpecs.length === 0 || renderedNodes.length === 0) return false;
  const renderedNodeKeys = new Set(renderedNodes.map((node) => node.id));
  return nodeSpecs.every((node) => renderedNodeKeys.has(node.key));
};

const readStoredPositions = (caseid) => {
  if (!caseid) return {};
  try {
    const current = JSON.parse(localStorage.getItem(`projectUnfoldingPositions:${STORAGE_VERSION}:${caseid}`) || '{}');
    return normalizeProjectDagNodePositions(current);
  } catch (_error) {
    return {};
  }
};

const readStoredHandles = (caseid) => {
  if (!caseid) return {};
  try {
    const current = JSON.parse(localStorage.getItem(`projectUnfoldingHandles:${STORAGE_VERSION}:${caseid}`) || '{}');
    return normalizeProjectDagHandlePositions(current);
  } catch (_error) {
    return {};
  }
};

// Progress changes presentation only. Geometry, local selection and measurements
// belong to the structural/interaction paths and are never rebuilt here.
export const updateProjectDagNodePresentation = (nodes, {
  runtimeByNode, nodeErrors, activitySequenceByNode, reduceMotion, reduceEffects,
  pageVisible, tagCount, receivedCount, reanalysis,
}) => {
  let animatedNodeCount = 0;
  let changed = false;
  const next = nodes.map((node) => {
    const spec = node.data.node;
    const error = nodeErrors[node.id]
      || (spec.kind === 'input' ? nodeErrors[`connector:${String(spec.item?.type || '').toLowerCase().replace(/[^a-z0-9]/g, '')}`] : null);
    const runtime = runtimeByNode[node.id];
    const animate = Boolean(runtime?.status === 'processing' && !reduceMotion
      && !reduceEffects && pageVisible && animatedNodeCount < MAX_ANIMATED_NODES);
    if (animate) animatedNodeCount += 1;
    const candidateRuntime = runtime ? { ...runtime, animate,
      activitySequence: activitySequenceByNode[node.id] || 0 } : runtime;
    const nextRuntime = areDagRecordsEqual(node.data.runtime, candidateRuntime)
      ? node.data.runtime : candidateRuntime;
    const nextReanalysis = areDagRecordsEqual(spec.reanalysis, reanalysis) ? spec.reanalysis : reanalysis;
    const candidateSpec = node.id === 'center' ? { ...spec, tagCount, receivedCount, reanalysis: nextReanalysis } : spec;
    const nextSpec = areDagRecordsEqual(spec, candidateSpec) ? spec : candidateSpec;
    if (node.data.error === error && node.data.runtime === nextRuntime
        && node.data.reduceEffects === reduceEffects && nextSpec === spec) return node;
    changed = true;
    return { ...node, data: { ...node.data, node: nextSpec, runtime: nextRuntime, error, reduceEffects } };
  });
  return changed ? next : nodes;
};

const UnfoldingCanvas = ({
  caseid, inputNodes, flowBranches, nodeErrors, nodeRuntime,
  nodeActivitySequence = EMPTY_NODE_ACTIVITY_SEQUENCE, loading,
  inputLoading = loading, outputLoading = loading, isEditorOpen,
  outputError = false, onNodeActivate, actionControls, canManageProject = false, locale, t,
  showDraftTutorial = false, templateSetupCurrentNodeKey = null,
  previewMode = false, previewFocusKey = null, buildFocusKey = null,
  previewNodeIdMap = null,
  tagCount = null, receivedCount = null, projectName = '',
  reanalysisPending = false, reanalysisJob = null, reanalysisRequestError = '',
  connectorMeasurementId = null, onInputsRendered,
}) => {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const completedConnectorMeasurementRef = useRef(null);
  const wasPreview = useRef(false);
  const previewLayout = useRef(null);
  const savePreviewLayout = useRef(false);
  const lastPreviewFocus = useRef(null);
  const seenEntranceNodeIds = useMemo(() => new Set(), [caseid]);
  const nodeInternalsBatcher = useMemo(() => ({ caseid, ...createDagNodeInternalsBatcher() }), [caseid]);
  useEffect(() => () => nodeInternalsBatcher.cancel(), [nodeInternalsBatcher]);
  const [nodes, setNodes] = useNodesState([]);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const logicalGroupDragRef = useRef(null);
  const [collapsedLogicalGroupIds, toggleLogicalGroupCollapse] = useCollapsedLogicalGroups(caseid);
  const updateHandleAnchors = useMemo(() => createDagHandleAnchorUpdater(), []);
  const [edges, setEdges, applyEdgesChange] = useEdgesState([]);
  const [actionSearchQuery, setActionSearchQuery] = useState('');
  const [helpSelectionActive, setHelpSelectionActive] = useState(false);
  const deferredSearchQuery = useDeferredValue(actionSearchQuery);
  const previousSearchIndexRef = useRef([]);
  const [collapsedFlowIds, setCollapsedFlowIds] = useState(() => new Set());
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [pendingActionMove, setPendingActionMove] = useState(null);
  const [draggedActionDropTargetKey, setDraggedActionDropTargetKey] = useState(null);
  const [draggedActionDropTargetEdgeId, setDraggedActionDropTargetEdgeId] = useState(null);
  const [isNodeDragging, setIsNodeDragging] = useState(false);
  const isNodeDraggingRef = useRef(false);
  const [isDraggingAction, setIsDraggingAction] = useState(false);
  const [isDraftTutorialOpen, setIsDraftTutorialOpen] = useState(() => {
    return Boolean(showDraftTutorial) && !isDraftTutorialDismissed();
  });
  const [draftTutorialStep, setDraftTutorialStep] = useState('connector');
  const [draftTutorialAnchor, setDraftTutorialAnchor] = useState(null);
  const [layoutLoading, setLayoutLoading] = useState(Boolean(caseid));
  const [layoutRevision, setLayoutRevision] = useState(0);
  const [layoutError, setLayoutError] = useState('');
  const [layoutReset, setLayoutReset] = useState(false);
  const [flowLabelHeights, setFlowLabelHeights] = useState({});
  const handleFlowLabelHeightChange = useCallback((key, height) => {
    setFlowLabelHeights((current) => current[key] === height ? current : { ...current, [key]: height });
  }, []);
  useEffect(() => setFlowLabelHeights({}), [caseid]);
  const [pageVisible, setPageVisible] = useState(
    typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const initiallyFittedCaseRef = useRef(null);
  const initiallyCenteredDraftRef = useRef(false);
  const canvasContainerRef = useRef(null);
  const layoutReadyCaseRef = useRef(null);
  const lastCenteredSearchRef = useRef(null);
  const storedPositionsRef = useRef({});
  const storedHandlesRef = useRef({});
  const storedHandleLayoutVersionRef = useRef(0);
  const templatePositionsRef = useRef({});
  const lastPersistedLayoutSignatureRef = useRef('');
  const layoutSaveRevisionRef = useRef(0);
  const layoutSaveQueueRef = useRef(Promise.resolve());
  const previousFlowBranchesRef = useRef([]);
  const flowBranchesCaseRef = useRef(null);
  const confirmedActionDropRef = useRef(null);

  useEffect(() => {
    const handleVisibility = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const toggleFlowCollapse = useCallback((flowId) => {
    const flowKey = flowId === undefined || flowId === null ? '' : String(flowId);
    if (!flowKey) return;
    setCollapsedFlowIds((current) => {
      const next = new Set(current);
      if (next.has(flowKey)) next.delete(flowKey);
      else next.add(flowKey);
      return next;
    });
  }, []);

  const dismissDraftTutorial = useCallback(() => {
    setIsDraftTutorialOpen(false);
    dismissDraftTutorialPreference();
  }, []);

  useEffect(() => {
    if (!showDraftTutorial || typeof window === 'undefined') return undefined;

    const handleDraftTutorialReset = (event) => {
      if (event?.type === 'storage' && event.key !== DRAFT_TUTORIAL_STORAGE_KEY) return;
      setDraftTutorialStep('connector');
      setIsDraftTutorialOpen(true);
    };
    window.addEventListener(DRAFT_TUTORIAL_RESET_EVENT, handleDraftTutorialReset);
    window.addEventListener('storage', handleDraftTutorialReset);
    return () => {
      window.removeEventListener(DRAFT_TUTORIAL_RESET_EVENT, handleDraftTutorialReset);
      window.removeEventListener('storage', handleDraftTutorialReset);
    };
  }, [showDraftTutorial]);

  const expandedPreviewFlows = useMemo(() => new Set(previewMode ? flowBranches
    .filter((branch) => branch.assistantPreviewExpanded || branch.nodes.some((node) => node.assistantPreview))
    .map((branch) => getFlowBranchKey(branch)) : []), [flowBranches, previewMode]);

  const collapsedActionNodeKeys = useMemo(() => {
    const keys = new Set();
    flowBranches.forEach((branch) => {
      if (expandedPreviewFlows.has(getFlowBranchKey(branch))) return;
      if (!collapsedFlowIds.has(getFlowBranchKey(branch))) return;
      branch.nodes
        .filter((node) => node.nodeType === 'action' && node.key)
        .forEach((node) => keys.add(node.key));
    });
    return keys;
  }, [collapsedFlowIds, expandedPreviewFlows, flowBranches]);

  const canvasActionControls = useMemo(() => {
    const controls = {
      ...(actionControls || {}),
      onToggleFlowCollapse: toggleFlowCollapse,
    };
    if (!showDraftTutorial) return controls;
    return {
      ...controls,
      draftTutorialStep,
      onAddConnector: (...args) => {
        setDraftTutorialStep('action');
        return actionControls?.onAddConnector?.(...args);
      },
      onAddAfter: (...args) => {
        setDraftTutorialStep('save');
        return actionControls?.onAddAfter?.(...args);
      },
    };
  }, [actionControls, draftTutorialStep, showDraftTutorial, toggleFlowCollapse]);

  const stagedFlowBranches = useMemo(() => flowBranches.map((branch) => {
    const stages = buildFlowExecutionStages(branch.nodes);
    const maximumStageSize = Math.max(1, ...stages.map((stage) => stage.nodes.length));
    return {
      ...branch,
      stages,
      bandHeight: NODE_HEIGHT + ((maximumStageSize - 1) * VERTICAL_GAP) + BRANCH_PADDING,
    };
  }), [flowBranches]);
  const totalBranchHeight = useMemo(
    () => stagedFlowBranches.reduce((total, branch) => total + branch.bandHeight, 0),
    [stagedFlowBranches]
  );
  const graphHeight = useMemo(() => Math.max(
    620,
    180 + Math.max(inputNodes.length, 1) * VERTICAL_GAP,
    totalBranchHeight + 100
  ), [inputNodes.length, totalBranchHeight]);
  const preparationNodes = useMemo(() => buildConnectorPreparationNodes(inputNodes), [inputNodes]);

  const defaultPositions = useMemo(() => {
    const positions = {};
    const inputY = verticalPositions(inputNodes.length, graphHeight);
    inputNodes.forEach((node, index) => { positions[node.key] = { x: 55, y: inputY[index] }; });
    const preparationWidth = Math.max(0, ...inputNodes.map((node) => node.preparationSteps?.length || 0)) * HORIZONTAL_GAP;
    preparationNodes.forEach((node) => {
      const source = positions[node.sourceNodeKey];
      positions[node.key] = { x: source.x + (node.preparation.index + 1) * HORIZONTAL_GAP, y: source.y };
    });
    positions.center = { x: 305 + preparationWidth, y: (graphHeight - PROJECT_WORKSPACE_NODE_HEIGHT) / 2 };
    let branchTop = Math.max(0, (graphHeight - totalBranchHeight) / 2);
    stagedFlowBranches.forEach((branch) => {
      branch.stages.forEach((stage, stageIndex) => {
        const stageHeight = NODE_HEIGHT + ((stage.nodes.length - 1) * VERTICAL_GAP);
        const stageTop = branchTop + ((branch.bandHeight - stageHeight) / 2);
        stage.nodes.forEach((node, nodeIndex) => {
          positions[node.key] = {
            x: getProjectDagActionsStart(positions.center.x, PROJECT_WORKSPACE_NODE_WIDTH)
              + DAG_SECTION_PADDING + DAG_FLOW_LABEL_WIDTH + stageIndex * HORIZONTAL_GAP,
            y: stageTop + nodeIndex * VERTICAL_GAP,
          };
        });
      });
      branchTop += branch.bandHeight;
    });
    return positions;
  }, [graphHeight, inputNodes, preparationNodes, stagedFlowBranches, totalBranchHeight]);
  const templateNodeKeys = useMemo(() => buildProjectDagTemplateNodeKeys(inputNodes), [inputNodes]);

  const flowLinkEndpointKeys = useMemo(() => new Set(
    buildFlowActionConnections(flowBranches, t).flatMap(({ source, target }) => [source, target])
  ), [flowBranches, t]);
  const currentNodePositionSignature = flowLinkPositionSignature(nodes, flowLinkEndpointKeys, defaultPositions);
  const currentNodePositions = useMemo(
    () => Object.fromEntries(JSON.parse(currentNodePositionSignature).map(([key, x, y]) => (
      [key, { x, y }]
    ))),
    [currentNodePositionSignature]
  );
  const humanOutcomePositions = useMemo(() => ({
    ...defaultPositions,
    ...(previewMode ? {} : storedPositionsRef.current),
    ...(previewMode ? {} : currentNodePositions),
  }), [currentNodePositions, defaultPositions, layoutRevision, previewMode]);
  const humanOutcomeConnections = useMemo(
    () => buildFlowActionConnections(flowBranches, t, humanOutcomePositions),
    [flowBranches, humanOutcomePositions, t]
  );

  const runtimeByNode = useMemo(() => {
    const values = { ...nodeRuntime };
    const actionRuntimes = [];
    flowBranches.forEach((branch) => {
      const branchRuntimes = branch.nodes
        .filter((node) => node.nodeType === 'action')
        .map((node) => nodeRuntime[node.key])
        .filter(Boolean);
      values[`flow:${branch.flow.id}`] = aggregateRuntime(branchRuntimes);
      actionRuntimes.push(...branchRuntimes);
    });
    values.center = aggregateRuntime(actionRuntimes);
    return values;
  }, [flowBranches, nodeRuntime]);
  const activitySequenceByNode = useMemo(() => {
    const values = { ...nodeActivitySequence };
    let projectActivitySequence = 0;
    flowBranches.forEach((branch) => {
      const branchSequence = branch.nodes
        .filter((node) => node.nodeType === 'action')
        .reduce((total, node) => total + (Number(nodeActivitySequence[node.key]) || 0), 0);
      values['flow:' + branch.flow.id] = branchSequence;
      projectActivitySequence += branchSequence;
    });
    values.center = projectActivitySequence;
    return values;
  }, [flowBranches, nodeActivitySequence]);

  const reanalysisRuntime = nodeRuntime?.['reanalysis:project'];
  const reanalysisError = nodeErrors?.['reanalysis:project'] || reanalysisRequestError;
  const graphNodeSpecs = useMemo(() => {
    const markCurrentTemplateSetupNode = (node) => ({
      ...node,
      templateSetupCurrent: node.key === templateSetupCurrentNodeKey,
    });
    return [
      ...inputNodes.map(markCurrentTemplateSetupNode),
      ...preparationNodes,
      {
        key: 'center',
        id: 'center',
        label: projectName || t('dag.project_name', 'My project'),
        kind: 'center',
        width: PROJECT_WORKSPACE_NODE_WIDTH,
        inputLoading,
        outputLoading,
      },
      ...flowBranches.flatMap((branch) => branch.nodes.map(markCurrentTemplateSetupNode)),
    ];
  }, [flowBranches, inputLoading, inputNodes, outputLoading, preparationNodes, projectName, t, templateSetupCurrentNodeKey]);
  const largeDag = graphNodeSpecs.length > LARGE_DAG_NODE_THRESHOLD;
  const reduceEffects = reduceMotion || shouldReduceDagEffects(
    graphNodeSpecs.length, typeof navigator === 'undefined' ? {} : navigator
  );
  const nodePresentation = useMemo(() => ({ runtimeByNode, nodeErrors, activitySequenceByNode,
    reduceMotion, reduceEffects, pageVisible, tagCount, receivedCount,
    reanalysis: { runtime: reanalysisRuntime, error: reanalysisError, pending: reanalysisPending, job: reanalysisJob },
  }), [runtimeByNode, nodeErrors, activitySequenceByNode, reduceMotion, reduceEffects, pageVisible,
    tagCount, receivedCount, reanalysisRuntime, reanalysisError, reanalysisPending, reanalysisJob]);
  const nodePresentationRef = useRef(nodePresentation);
  nodePresentationRef.current = nodePresentation;
  const graphFitKey = useMemo(
    () => `${caseid == null ? '' : caseid}:${graphNodeSpecs.map((node) => node.key).join('|')}`,
    [caseid, graphNodeSpecs]
  );
  const inputConnections = useMemo(() => buildProjectDagInputConnections(inputNodes), [inputNodes]);
  const flowStageConnections = useMemo(() => stagedFlowBranches.flatMap((branch) => {
    const nodesByKey = new Map(branch.nodes.map((node) => [node.key, node]));
    const nodeIndexes = new Map(branch.nodes.map((node, index) => [node.key, index]));
    return buildFlowStageConnections(branch).map((connection) => ({ connection,
      node: nodesByKey.get(connection.target), flow: branch.flow,
      previousFlatNode: branch.nodes[nodeIndexes.get(connection.target) - 1] || null,
    }));
  }), [stagedFlowBranches]);
  const graphConnections = useMemo(() => [
    ...inputConnections,
    ...flowStageConnections.map(({ connection }) => connection),
    ...humanOutcomeConnections,
  ], [humanOutcomeConnections, inputConnections, flowStageConnections]);
  const handleConnectionIndex = useMemo(
    () => buildDagHandleConnectionIndex(graphConnections), [graphConnections]
  );
  const graphNodeKeys = useMemo(
    () => new Set(graphNodeSpecs.map((node) => node.key)),
    [graphNodeSpecs]
  );
  const searchableDagNodes = useMemo(() => graphNodeSpecs.filter((node) => (
    node.kind === 'input'
    || node.kind === 'preparation'
    || node.kind === 'system'
    || node.kind === 'output'
    || node.nodeType === 'action'
  )), [graphNodeSpecs]);
  const searchableDagNodeKeys = useMemo(
    () => new Set(searchableDagNodes.map((node) => node.key)),
    [searchableDagNodes]
  );
  const normalizedDagSearchQuery = deferredSearchQuery.trim().toLowerCase();
  const isDagSearchActive = Boolean(normalizedDagSearchQuery);
  const searchableDagNodeIndex = useMemo(() => (
    isDagSearchActive
      ? buildDagNodeSearchIndex(searchableDagNodes, previousSearchIndexRef.current)
      : previousSearchIndexRef.current
  ), [isDagSearchActive, searchableDagNodes]);
  useEffect(() => {
    previousSearchIndexRef.current = searchableDagNodeIndex;
  }, [searchableDagNodeIndex]);
  const dagSearchMatchKeys = useMemo(
    () => findDagNodeSearchMatchKeysInIndex(searchableDagNodeIndex, normalizedDagSearchQuery),
    [normalizedDagSearchQuery, searchableDagNodeIndex]
  );
  const dagSearchMatchCount = normalizedDagSearchQuery
    ? dagSearchMatchKeys.size
    : searchableDagNodes.length;
  const uniqueDagSearchMatchKey = dagSearchMatchKeys.size === 1
    ? Array.from(dagSearchMatchKeys)[0]
    : null;

  useEffect(() => {
    if (!normalizedDagSearchQuery || dagSearchMatchKeys.size === 0) return;
    const matchingCollapsedFlowIds = new Set();
    flowBranches.forEach((branch) => {
      const flowKey = getFlowBranchKey(branch);
      if (
        collapsedFlowIds.has(flowKey)
        && branch.nodes.some((node) => dagSearchMatchKeys.has(node.key))
      ) {
        matchingCollapsedFlowIds.add(flowKey);
      }
    });
    if (matchingCollapsedFlowIds.size === 0) return;
    setCollapsedFlowIds((current) => {
      const next = new Set(current);
      matchingCollapsedFlowIds.forEach((flowKey) => next.delete(flowKey));
      return next;
    });
  }, [collapsedFlowIds, dagSearchMatchKeys, flowBranches, normalizedDagSearchQuery]);

  useEffect(() => {
    const localPositions = readStoredPositions(caseid);
    const localHandles = readStoredHandles(caseid);
    storedPositionsRef.current = localPositions;
    storedHandlesRef.current = localHandles;
    storedHandleLayoutVersionRef.current = Object.keys(localHandles).length > 0 ? 2 : 0;
    templatePositionsRef.current = {};
    lastPersistedLayoutSignatureRef.current = '';
    layoutSaveRevisionRef.current += 1;
    previousFlowBranchesRef.current = [];
    wasPreview.current = false;
    previewLayout.current = null;
    savePreviewLayout.current = false;
    flowBranchesCaseRef.current = caseid == null ? null : String(caseid);
    initiallyFittedCaseRef.current = null;
    initiallyCenteredDraftRef.current = false;
    layoutReadyCaseRef.current = null;
    lastCenteredSearchRef.current = null;
    previousSearchIndexRef.current = [];
    setActionSearchQuery('');
    setLayoutError('');
    setNodes([]);
    setCollapsedFlowIds(new Set());
    setPendingActionMove(null);
    setDraggedActionDropTargetKey(null);
    setDraggedActionDropTargetEdgeId(null);
    setIsNodeDragging(false);
    isNodeDraggingRef.current = false;
    logicalGroupDragRef.current = null;
    setIsDraggingAction(false);
    confirmedActionDropRef.current = null;
    if (!caseid) {
      setLayoutLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLayoutLoading(true);
    fetchProjectDagLayout(caseid)
      .then(async (payload) => {
        if (cancelled) return;
        const canvasLayout = payload?.canvas_layout;
        const hasPersistedLayout = Boolean(
          canvasLayout
          && typeof canvasLayout === 'object'
          && Object.prototype.hasOwnProperty.call(canvasLayout, 'nodes')
        );
        const serverPositions = normalizeProjectDagNodePositions(canvasLayout?.nodes);
        const serverHandles = normalizeProjectDagHandlePositions(canvasLayout?.handles);
        const templatePositions = normalizeProjectDagNodePositions(canvasLayout?.template_nodes);
        const nextPositions = hasPersistedLayout ? serverPositions : localPositions;
        const nextHandles = hasPersistedLayout ? serverHandles : localHandles;
        storedPositionsRef.current = nextPositions;
        storedHandlesRef.current = nextHandles;
        storedHandleLayoutVersionRef.current = hasPersistedLayout
          ? Number(canvasLayout?.version || 0)
          : (Object.keys(localHandles).length > 0 ? 2 : 0);
        templatePositionsRef.current = templatePositions;
        lastPersistedLayoutSignatureRef.current = storedHandleLayoutVersionRef.current >= 2
          ? buildProjectDagLayoutSignature({
            nodes: nextPositions,
            handles: nextHandles,
          })
          : '';
        localStorage.setItem(
          `projectUnfoldingPositions:${STORAGE_VERSION}:${caseid}`,
          JSON.stringify(nextPositions)
        );
        localStorage.setItem(
          `projectUnfoldingHandles:${STORAGE_VERSION}:${caseid}`,
          JSON.stringify(nextHandles)
        );
        setLayoutRevision((revision) => revision + 1);

        if (!hasPersistedLayout && canManageProject && Object.keys(localPositions).length > 0) {
          try {
            await updateProjectDagLayout(caseid, localPositions, localHandles);
          } catch (error) {
            if (!cancelled) {
              console.error('Failed to migrate project DAG layout:', error);
              const fallback = t('dag.layout_save_error', 'Node positions could not be saved.');
              const reason = String(error?.message || '').trim().slice(0, 180);
              setLayoutError(reason ? fallback + ' (' + reason + ')' : fallback);
            }
          }
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load project DAG layout:', error);
        setLayoutError(t('dag.layout_load_error', 'Saved node positions could not be loaded.'));
        setLayoutRevision((revision) => revision + 1);
      })
      .finally(() => {
        if (!cancelled) {
          layoutReadyCaseRef.current = String(caseid);
          setLayoutLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canManageProject, caseid, setNodes, t]);

  useEffect(() => {
    if (!connectorMeasurementId || !onInputsRendered || inputLoading || layoutLoading
        || !reactFlowInstance || completedConnectorMeasurementRef.current === connectorMeasurementId) return undefined;
    const expected = new Set(inputNodes.map((node) => node.key));
    for (const node of nodes) {
      if (expected.has(node.id) && (node.measured?.width || node.width) > 0) expected.delete(node.id);
    }
    if (expected.size) return undefined;
    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        completedConnectorMeasurementRef.current = connectorMeasurementId;
        onInputsRendered(connectorMeasurementId, inputNodes.length);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame != null) cancelAnimationFrame(secondFrame);
    };
  }, [connectorMeasurementId, inputLoading, inputNodes, layoutLoading, nodes, onInputsRendered, reactFlowInstance]);

  useEffect(() => {
    const isEmptyDraft = caseid == null && inputNodes.length === 0;
    if (
      !isEmptyDraft
      || !reactFlowInstance
      || loading
      || inputLoading
      || outputLoading
      || !areProjectDagNodesReadyForFit(graphNodeSpecs, nodes)
      || initiallyCenteredDraftRef.current
    ) return undefined;

    // The empty home draft contains the DataUnfold anchor and a placeholder
    // Main Flow. Center the anchor itself so the first meaningful object is
    // visually centered in the build area instead of centering the whole
    // placeholder graph.
    let centerFrame;
    const measurementFrame = requestAnimationFrame(() => {
      centerFrame = requestAnimationFrame(() => {
        const centerNode = reactFlowInstance.getNode('center');
        if (!centerNode) return;
        initiallyCenteredDraftRef.current = true;
        const position = centerNode.positionAbsolute || centerNode.position;
        reactFlowInstance.setCenter(
          position.x + (PROJECT_WORKSPACE_NODE_WIDTH / 2),
          position.y + (PROJECT_WORKSPACE_NODE_HEIGHT / 2),
          { zoom: 0.9, duration: 240 }
        );
      });
    });

    return () => {
      cancelAnimationFrame(measurementFrame);
      if (centerFrame !== undefined) cancelAnimationFrame(centerFrame);
    };
  }, [caseid, graphNodeSpecs, inputLoading, inputNodes.length, loading, nodes, outputLoading, reactFlowInstance]);

  useEffect(() => {
    const caseKey = caseid == null ? null : String(caseid);
    if (previewMode) { wasPreview.current = true; return; }
    if (flowBranchesCaseRef.current !== caseKey) {
      flowBranchesCaseRef.current = caseKey;
      previousFlowBranchesRef.current = [];
    }
    if (!caseKey || layoutReadyCaseRef.current !== caseKey) return;
    if (confirmedActionDropRef.current) {
      // A confirmed drag already persisted the exact mouse-up coordinates.
      // Do not run the generic order remapper over that manual placement.
      previousFlowBranchesRef.current = flowBranches;
      confirmedActionDropRef.current = null;
      return;
    }

    const committedPreview = wasPreview.current && previewLayout.current?.committed;
    const currentNodePositions = Object.fromEntries(
      (wasPreview.current && !committedPreview ? [] : nodes.filter((node) => !node.data?.node?.assistantPreview))
        .map((node) => [node.id, node.position])
    );
    wasPreview.current = false;
    const sourcePositions = { ...storedPositionsRef.current,
      ...(committedPreview ? previewLayout.current.positions : {}), ...currentNodePositions };
    const remappedPositions = committedPreview ? sourcePositions : remapProjectDagActionPositionsForOrder(
      sourcePositions,
      previousFlowBranchesRef.current,
      flowBranches,
      confirmedActionDropRef.current?.nodeId
        ? [confirmedActionDropRef.current.nodeId]
        : []
    );
    if (remappedPositions !== sourcePositions || committedPreview) {
      const realKeys = new Set(nodes.filter((node) => !node.data?.node?.assistantPreview).map((node) => node.id));
      storedPositionsRef.current = committedPreview
        ? Object.fromEntries(Object.entries(remappedPositions).filter(([key]) => realKeys.has(key))) : remappedPositions;
      if (committedPreview) savePreviewLayout.current = true;
      if (committedPreview) initiallyFittedCaseRef.current = graphFitKey;
      // Handle anchors are derived from geometry and must follow the swapped nodes.
      storedHandlesRef.current = {};
      storedHandleLayoutVersionRef.current = 0;
      localStorage.setItem(
        `projectUnfoldingPositions:${STORAGE_VERSION}:${caseid}`,
        JSON.stringify(storedPositionsRef.current)
      );
      localStorage.setItem(
        `projectUnfoldingHandles:${STORAGE_VERSION}:${caseid}`,
        JSON.stringify({})
      );
    }
    previousFlowBranchesRef.current = flowBranches;
    previewLayout.current = null;
    if (confirmedActionDropRef.current) {
      confirmedActionDropRef.current = null;
    }
  }, [caseid, flowBranches, graphFitKey, layoutLoading, nodes, previewMode]);

  useEffect(() => {
    setNodes((currentNodes) => {
      const currentNodesById = new Map(currentNodes.map((node) => [node.id, node]));
      Object.entries(previewNodeIdMap || {}).forEach(([temporary, actual]) => {
        if (!currentNodesById.has(actual) && currentNodesById.has(temporary)) {
          currentNodesById.set(actual, currentNodesById.get(temporary));
        }
      });
      let previewPositions = null;
      if (previewMode) {
        const previous = previewLayout.current?.caseid === caseid ? previewLayout.current : null;
        const visiblePositions = Object.fromEntries(currentNodes.map((node) => [node.id, node.position]));
        const positions = { ...(previous?.positions || storedPositionsRef.current), ...visiblePositions };
        const actualIds = new Set(Object.values(previewNodeIdMap || {}));
        const committed = graphNodeSpecs.some((node) => actualIds.has(node.key));
        if (committed) Object.entries(previewNodeIdMap || {}).forEach(([temporary, actual]) => {
          if (positions[temporary]) positions[actual] = positions[temporary];
        });
        previewPositions = committed ? positions : remapProjectDagActionPositionsForOrder(
          positions, previous?.branches || previousFlowBranchesRef.current, flowBranches);
        previewLayout.current = { caseid, positions: previewPositions, branches: flowBranches, committed };
      }
      const basePosition = (node) => {
        const templateKey = getProjectDagTemplateNodeKey(node, inputNodes, templateNodeKeys);
        return (previewMode ? previewPositions[node.key] : storedPositionsRef.current[node.key])
          || (templateKey ? templatePositionsRef.current[templateKey] : null)
          || defaultPositions[node.key] || { x: 0, y: 0 };
      };
      const sourcePositions = new Map(inputNodes.map((node) => [node.key, basePosition(node)]));
      const centerPosition = basePosition({ key: 'center' });
      // Old saved layouts have a direct connector-to-center edge. Make room
      // for actual preparation steps without changing their execution/order.
      const preparationShift = (previewMode && currentNodesById.has('center'))
        || (storedPositionsRef.current.center && preparationNodes.every((node) => storedPositionsRef.current[node.key])) ? 0 : Math.max(0, ...inputNodes.filter((node) => node.preparationSteps?.length)
        .map((node) => sourcePositions.get(node.key).x
          + (node.preparationSteps.length + 1) * HORIZONTAL_GAP - centerPosition.x));
      const nodePosition = (node) => {
        if (node.kind === 'preparation') {
          if (!previewMode && storedPositionsRef.current[node.key]) return storedPositionsRef.current[node.key];
          const source = sourcePositions.get(node.sourceNodeKey);
          return { x: source.x + (node.preparation.index + 1) * HORIZONTAL_GAP, y: source.y };
        }
        const position = basePosition(node);
        return node.key === 'center' || ['action', 'flow', 'add'].includes(node.nodeType) || ['action', 'output', 'add'].includes(node.kind)
          ? { ...position, x: position.x + preparationShift } : position;
      };
      const nextNodes = graphNodeSpecs.map((node, animationIndex) => {
        return {
          id: node.key,
          type: 'unfolding',
          // Keep measured bounds through data updates so the minimap does not
          // drop nodes while React Flow measures them again.
          measured: currentNodesById.get(node.key)?.measured,
          extent: currentNodesById.get(node.key)?.extent,
          position: nodePosition(node),
          draggable: canManageProject && !isEditorOpen && !previewMode,
          deletable: false,
          connectable: false,
          selectable: true,
          focusable: !previewMode,
          ariaLabel: node.label,
          dragHandle: node.nodeType === 'action' || node.kind === 'preparation' ? '.dag-action-node-drag-handle' : undefined,
          data: {
            node: node.nodeType === 'flow'
              ? { ...node, flowCollapsed: collapsedFlowIds.has(String(node.id)) && !expandedPreviewFlows.has(String(node.id)) }
              : node,
            animationIndex,
            actionControls: canvasActionControls,
            search: searchableDagNodeKeys.has(node.key) ? {
              active: Boolean(normalizedDagSearchQuery),
              match: dagSearchMatchKeys.has(String(node.key)),
            } : null,
            locale,
            t,
          },
          hidden: collapsedActionNodeKeys.has(node.key),
          zIndex: node.key === templateSetupCurrentNodeKey ? 5 : 2,
          style: { width: node.width || NODE_WIDTH, ...(node.assistantPreview ? {
            outline: '2px dashed #7777d0', outlineOffset: 5, borderRadius: 18,
          } : {}) },
        };
      });
      const canRestorePersistedHandles = graphNodeSpecs.every((node) => (
        storedHandleLayoutVersionRef.current >= 2
        && humanOutcomeConnections.length === 0
        && storedPositionsRef.current[node.key]
        && storedHandlesRef.current[node.key]
      ));
      const preferredHandleAnchors = canRestorePersistedHandles
        ? Object.fromEntries(Object.entries(storedHandlesRef.current).filter(([nodeId]) => (
          nodeId !== 'center'
        )))
        : {};
      const fixedKeys = new Set(currentNodes.length
        ? currentNodesById.keys() : Object.keys(storedPositionsRef.current));
      const placedNodes = placeNewProjectDagNodes(constrainProjectDagSections(alignProjectDagFlowRows(
          nextNodes, stagedFlowBranches, NODE_WIDTH, flowLabelHeights,
          new Set(Object.keys(storedPositionsRef.current))
        ), NODE_WIDTH, {
          flowBranches: stagedFlowBranches, preserveSourceSpacing: true,
        }), fixedKeys);
      if (placedNodes.some((node) => !fixedKeys.has(node.id))) {
        const placedPositions = Object.fromEntries(placedNodes.map((node) => [node.id, node.position]));
        if (previewMode && previewLayout.current) {
          previewLayout.current.positions = { ...previewLayout.current.positions, ...placedPositions };
        } else if (currentNodes.length) {
          storedPositionsRef.current = { ...storedPositionsRef.current, ...placedPositions };
        }
      }
      return reuseDagElements(currentNodes, updateProjectDagNodePresentation(updateHandleAnchors(
        placedNodes, graphConnections, preferredHandleAnchors, handleConnectionIndex
      ), nodePresentationRef.current));
    });
  }, [
    actionControls, canManageProject, canvasActionControls, collapsedActionNodeKeys, collapsedFlowIds,
    dagSearchMatchKeys, defaultPositions, flowLabelHeights, graphNodeSpecs,
    graphConnections, handleConnectionIndex, humanOutcomeConnections, isEditorOpen, layoutRevision, normalizedDagSearchQuery,
    inputNodes, preparationNodes, locale,
    searchableDagNodeKeys, setNodes, t, templateNodeKeys, templateSetupCurrentNodeKey, updateHandleAnchors,
    previewMode, previewNodeIdMap, caseid, flowBranches, expandedPreviewFlows, stagedFlowBranches,
  ]);

  useEffect(() => {
    setNodes((current) => updateProjectDagNodePresentation(current, nodePresentation));
  }, [nodePresentation, setNodes]);

  const pendingActionTargetKey = (
    pendingActionMove?.targetNode?.key || draggedActionDropTargetKey || null
  );
  const pendingActionTargetEdgeId = (
    pendingActionMove?.targetEdgeId || draggedActionDropTargetEdgeId || null
  );

  const graphEdges = useMemo(() => {
    const preparationByKey = new Map(preparationNodes.map((node) => [node.key, node]));
    const calledFlowNodeKeys = new Set(humanOutcomeConnections.map((connection) => connection.target));
    let animatedEdgeCount = 0;
    const createEdge = (
      source,
      target,
      runtime = null,
      error = null,
      controls = null,
      visualOptions = null,
      targetHandle = 'target'
    ) => {
      const visual = resolveDagEdgeVisual(runtime, error, visualOptions || {});
      const presentation = resolveDagEdgePresentation(visual);
      const dropTarget = visualOptions?.dropTarget === true;
      const edgeColor = visualOptions?.color || visual.color;
      const hideConnection = visualOptions?.hideConnection === true;
      const animateGradient = (
        !hideConnection && visual.state === 'active'
        && !reduceMotion
        && !reduceEffects
        && pageVisible
        && animatedEdgeCount < MAX_ANIMATED_EDGES
      );
      const animateQueuedPulse = (
        !hideConnection && visual.state === 'queued'
        && !reduceMotion
        && !reduceEffects
        && pageVisible
        && animatedEdgeCount < MAX_ANIMATED_EDGES
      );
      const activitySequence = Number(visualOptions?.activitySequence) || 0;
      const animatePackets = animateGradient && activitySequence > 0;
      if (animateGradient || animateQueuedPulse) animatedEdgeCount += 1;
      const markerSize = resolveDagEdgeMarkerSize(presentation.strokeWidth);
      return {
        id: `${source}:${target}${targetHandle === 'target' ? '' : `:${targetHandle}`}`,
        source,
        target,
        sourceHandle: visualOptions?.sourceHandle || 'source',
        targetHandle,
        deletable: false,
        type: 'unfolding',
        focusable: !hideConnection,
        ...(hideConnection ? { ariaLabel: controls?.flowAriaLabel } : {}),
        hidden: collapsedActionNodeKeys.has(source) || collapsedActionNodeKeys.has(target),
        zIndex: 0,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: markerSize,
          height: markerSize,
          color: dropTarget ? EDGE_COLORS.dropTarget : edgeColor,
        },
        data: {
          hideConnection,
          color: dropTarget ? EDGE_COLORS.dropTarget : edgeColor,
          state: visual.state,
          active: visual.active,
          animateGradient,
          animateQueuedPulse,
          animatePackets,
          activitySequence,
          ...presentation,
          ...(visualOptions?.outcomeBranch ? {
            opacity: 0.96,
            strokeDasharray: '6 5',
            outcomeLabel: visualOptions.outcomeLabel,
          } : {}),
          strokeWidth: dropTarget ? Math.max(presentation.strokeWidth, 4.4) : presentation.strokeWidth,
          dropTarget,
          animateDropTarget: dropTarget && !reduceMotion && !reduceEffects && pageVisible,
          reduceMotion: reduceMotion || reduceEffects,
          ...controls,
        },
      };
    };
    return [
      ...inputConnections.map(({ source, target, node }) => {
        const preparationNode = preparationByKey.get(source) || preparationByKey.get(target);
        const runtime = runtimeByNode[node.key];
        const isReanalysis = node.nodeType === 'reanalysis';
        const error = nodeErrors[node.key]
          || (!isReanalysis
            ? nodeErrors[`connector:${String(node.item?.type || '').toLowerCase().replace(/[^a-z0-9]/g, '')}`]
            : null);
        const configurationBlocked = !isReanalysis && (
          runtime?.errorKind === 'configuration'
          || (Boolean(error) && !runtime?.errorKind)
        );
        return createEdge(
          source,
          target,
          runtime,
          configurationBlocked ? null : error,
          {
            clickable: true,
            journalLabel: preparationNode ? t('connector_setup.step.open') : t('dag.journal_retained_history', 'Retained execution history'),
            onOpenJournal: preparationNode ? () => onNodeActivate?.(preparationNode) : actionControls?.onOpenIngestionJournal,
            journalNode: buildConnectorEdgeJournalNode(node),
          },
          {
            configurationBlocked,
            activitySequence: activitySequenceByNode[source] || 0,
            dropTarget: isProjectDagDropTargetEdge(
              source,
              target,
              pendingActionTargetKey,
              pendingActionTargetEdgeId
            ),
          }
        );
      }),
      ...flowStageConnections.map(({ connection, node, previousFlatNode, flow }) => {
          const isManualFlow = node.nodeType === 'flow'
            && normalizeFlowTriggerMode(node.item?.trigger_mode, node.item?.auto_run_enabled) === 'disabled';
          const currentGroup = node?.actionMeta?.executionGroup || null;
          const canGroupWithPrevious = Boolean(
            actionControls?.canManage
            && node?.nodeType === 'action'
            && previousFlatNode?.nodeType === 'action'
            && connection.source === previousFlatNode.key
            && !currentGroup
          );
          return createEdge(
            connection.source,
            node.key,
            runtimeByNode[node.key],
            nodeErrors[node.key],
            {
              ...(canGroupWithPrevious ? {
                canGroupWithPrevious: true,
                groupLabel: t('workflow.group_parallel_with_previous', 'Run with previous action in parallel'),
                onGroupWithPrevious: actionControls.onGroupWithPrevious,
                actionId: node.id,
              } : {}),
              ...(connection.source === 'center' && node.nodeType === 'flow' ? {
                flowLabel: node.label,
                onFlowLabelHeightChange: handleFlowLabelHeightChange,
                flowItem: flow || node.item,
                flowLabelAriaLabel: t('workflow.automation_flow_name_label', 'Flow name'),
                flowTriggerLabel: isManualFlow && calledFlowNodeKeys.has(node.key)
                  ? t('dag.flow_started_by_another_flow', 'Started by another flow')
                  : getFlowTriggerModeLabel(node.item?.trigger_mode, t, node.item?.auto_run_enabled),
                flowTriggerConditionSummary: getFlowTriggerConditionSummary(node.item, t, locale),
                flowClickable: typeof onNodeActivate === 'function',
                flowAriaLabel: node.label,
                onOpenFlow: onNodeActivate,
                flowNode: node,
                onRenameFlow: actionControls?.canManage
                  && typeof actionControls?.onRenameFlow === 'function'
                  ? actionControls.onRenameFlow
                  : null,
              } : {}),
            },
            {
              activitySequence: activitySequenceByNode[node.key] || 0,
              hideConnection: connection.source === 'center' && isManualFlow,
              dropTarget: isProjectDagDropTargetEdge(
                connection.source,
                connection.target,
                pendingActionTargetKey,
                pendingActionTargetEdgeId
              ),
            }
          );
      }),
      ...humanOutcomeConnections.map((connection) => {
        const color = ['shared', 'flow-call'].includes(connection.outcome) ? '#5757b0'
          : connection.outcome === 'approved' ? '#328d5d' : '#bf7930';
        return createEdge(
          connection.source,
          connection.target,
          null,
          null,
          null,
          { color, outcomeBranch: true, outcomeLabel: connection.outcomeLabel, sourceHandle: connection.sourceHandle },
          connection.targetHandle || 'target'
        );
      }),
    ];
  }, [
    actionControls, collapsedActionNodeKeys, inputConnections, locale, nodeErrors, onNodeActivate,
    activitySequenceByNode, pageVisible, pendingActionTargetEdgeId, pendingActionTargetKey, reduceMotion, reduceEffects, runtimeByNode,
    humanOutcomeConnections, preparationNodes, flowStageConnections, t, handleFlowLabelHeightChange
  ]);

  useEffect(() => setEdges((current) => reuseDagElements(current, graphEdges)), [graphEdges, setEdges]);

  const handleNodesChange = useCallback((changes) => {
    const structuralChanges = keepProjectDagStructuralElements(changes)
      .filter((change) => !change.id?.startsWith('logical-group:'));
    if (structuralChanges.length === 0) return;
    const changedNodeKeys = structuralChanges
      .filter((change) => change.type === 'position' && change.position)
      .map((change) => change.id);
    if (changedNodeKeys.length) {
      // An earlier HTTP acknowledgement must not replace a newer pointer or
      // keyboard edit before that edit reaches its own save.
      layoutSaveRevisionRef.current += 1;
      setLayoutReset(false);
    }
    setNodes((current) => {
      const changed = applyNodeChanges(structuralChanges, current);
      const next = changedNodeKeys.length ? constrainProjectDagSections(changed, NODE_WIDTH, {
        movingCenter: changedNodeKeys.includes('center'),
      }) : changed;
      if (changedNodeKeys.length) {
        // Freeze the whole visible layout on manual movement. Runtime and
        // label updates must not realign neighboring branches or undo a drag.
        storedPositionsRef.current = { ...storedPositionsRef.current,
          ...Object.fromEntries(next.map((node) => [node.id, node.position])) };
      }
      // Selection and measurements cannot change our fixed handle geometry.
      return changedNodeKeys.length > 0
        ? applyDynamicHandleAnchors(next, graphConnections, {}, handleConnectionIndex, changedNodeKeys)
        : next;
    });
  }, [graphConnections, handleConnectionIndex, setNodes]);

  const handleEdgesChange = useCallback((changes) => {
    applyEdgesChange(keepProjectDagStructuralElements(changes));
  }, [applyEdgesChange]);

  useEffect(() => {
    const caseKey = caseid == null ? null : String(caseid);

    if (
      !caseKey
      || previewMode
      || !reactFlowInstance
      || loading
      || inputLoading
      || outputLoading
      || layoutLoading
      || layoutReadyCaseRef.current !== caseKey
      || !areProjectDagNodesReadyForFit(graphNodeSpecs, nodes)
      || initiallyFittedCaseRef.current === graphFitKey
    ) return undefined;

    // Wait for React Flow to measure the current node set before calculating
    // its bounds. A second fit is allowed when deferred actions arrive.
    let fitFrame;
    const measurementFrame = requestAnimationFrame(() => {
      fitFrame = requestAnimationFrame(() => {
        initiallyFittedCaseRef.current = graphFitKey;
        reactFlowInstance.fitView({ padding: 0.16, duration: 320, maxZoom: 1.1 });
      });
    });
    return () => {
      cancelAnimationFrame(measurementFrame);
      if (fitFrame !== undefined) cancelAnimationFrame(fitFrame);
    };
  }, [
    caseid, graphFitKey, graphNodeSpecs, inputLoading, layoutLoading, loading, nodes,
    outputLoading, previewMode, reactFlowInstance,
  ]);

  useEffect(() => {
    if (!previewMode || !previewFocusKey) { lastPreviewFocus.current = null; return undefined; }
    if (!reactFlowInstance || layoutLoading || lastPreviewFocus.current === previewFocusKey) return undefined;
    const node = nodes.find((candidate) => candidate.id === previewFocusKey);
    if (!node?.measured?.width) return undefined;
    const frame = requestAnimationFrame(() => {
      lastPreviewFocus.current = previewFocusKey;
      reactFlowInstance.setCenter(
        node.position.x + NODE_WIDTH / 2, node.position.y + NODE_HEIGHT / 2,
        { zoom: Math.min(reactFlowInstance.getZoom(), 0.9), duration: reduceMotion ? 0 : 200 },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [layoutLoading, nodes, previewFocusKey, previewMode, reactFlowInstance, reduceMotion]);

  useEffect(() => {
    if (!reactFlowInstance || !uniqueDagSearchMatchKey) {
      if (!uniqueDagSearchMatchKey) lastCenteredSearchRef.current = null;
      return;
    }
    const searchKey = `${caseid}:${normalizedDagSearchQuery}:${uniqueDagSearchMatchKey}`;
    if (lastCenteredSearchRef.current === searchKey) return;
    const matchingNode = reactFlowInstance.getNode(uniqueDagSearchMatchKey);
    if (!matchingNode) return;

    lastCenteredSearchRef.current = searchKey;
    const position = matchingNode.positionAbsolute || matchingNode.position;
    reactFlowInstance.setCenter(
      position.x + (NODE_WIDTH / 2),
      position.y + (NODE_HEIGHT / 2),
      {
        zoom: Math.max(reactFlowInstance.getZoom(), 1),
        duration: 320,
      }
    );
  }, [
    caseid, normalizedDagSearchQuery, reactFlowInstance, uniqueDagSearchMatchKey
  ]);

  const persistPositions = useCallback((positions) => {
    if (!caseid || previewMode) return Promise.resolve();
    const snapshot = buildProjectDagLayoutSnapshot(
      graphNodeSpecs,
      positions,
      graphConnections
    );
    storedPositionsRef.current = snapshot.nodes;
    storedHandlesRef.current = snapshot.handles;
    storedHandleLayoutVersionRef.current = 2;
    lastPersistedLayoutSignatureRef.current = buildProjectDagLayoutSignature(snapshot);
    localStorage.setItem(
      `projectUnfoldingPositions:${STORAGE_VERSION}:${caseid}`,
      JSON.stringify(snapshot.nodes)
    );
    localStorage.setItem(
      `projectUnfoldingHandles:${STORAGE_VERSION}:${caseid}`,
      JSON.stringify(snapshot.handles)
    );
    if (!canManageProject) return Promise.resolve();

    setLayoutError('');
    const saveRevision = ++layoutSaveRevisionRef.current;
    const savePromise = layoutSaveQueueRef.current
      .catch(() => undefined)
      .then(() => updateProjectDagLayout(caseid, snapshot.nodes, snapshot.handles));
    layoutSaveQueueRef.current = savePromise.catch(() => undefined);
    return savePromise
      .then((payload) => {
        if (saveRevision !== layoutSaveRevisionRef.current) return;
        const savedPositions = normalizeProjectDagNodePositions(payload?.canvas_layout?.nodes);
        const hasSavedHandles = Object.prototype.hasOwnProperty.call(
          payload?.canvas_layout || {},
          'handles'
        );
        const savedHandles = hasSavedHandles
          ? normalizeProjectDagHandlePositions(payload?.canvas_layout?.handles)
          : snapshot.handles;
        storedPositionsRef.current = savedPositions;
        storedHandlesRef.current = savedHandles;
        storedHandleLayoutVersionRef.current = Number(payload?.canvas_layout?.version || 2);
        lastPersistedLayoutSignatureRef.current = buildProjectDagLayoutSignature({
          nodes: savedPositions,
          handles: savedHandles,
        });
        localStorage.setItem(
          `projectUnfoldingPositions:${STORAGE_VERSION}:${caseid}`,
          JSON.stringify(savedPositions)
        );
        localStorage.setItem(
          `projectUnfoldingHandles:${STORAGE_VERSION}:${caseid}`,
          JSON.stringify(savedHandles)
        );
      })
      .catch((error) => {
        if (saveRevision !== layoutSaveRevisionRef.current) return;
        console.error('Failed to save project DAG layout:', error);
        const fallback = t('dag.layout_save_error', 'Node positions could not be saved.');
        const reason = String(error?.message || '').trim().slice(0, 180);
        setLayoutError(reason ? fallback + ' (' + reason + ')' : fallback);
      });
  }, [canManageProject, caseid, graphConnections, graphNodeSpecs, previewMode, t]);

  const handleGroupDragStart = useCallback((group) => {
    if (!canManageProject || previewMode || isEditorOpen || layoutLoading || loading || inputLoading || outputLoading || pendingActionMove
      || helpSelectionActive || isNodeDraggingRef.current) return false;
    layoutSaveRevisionRef.current += 1;
    const keys = new Set(group.nodeKeys || group.members.map((member) => member.key));
    const current = nodesRef.current;
    logicalGroupDragRef.current = new Map(current.filter((node) => keys.has(node.id)).map((node) => [node.id, node.position]));
    storedPositionsRef.current = { ...storedPositionsRef.current,
      ...Object.fromEntries(current.map((node) => [node.id, node.position])) };
    isNodeDraggingRef.current = true;
    setIsNodeDragging(true);
    setLayoutReset(false);
    return true;
  }, [canManageProject, previewMode, isEditorOpen, layoutLoading, loading, inputLoading, outputLoading, pendingActionMove, helpSelectionActive]);

  const moveGroupFromStart = useCallback((delta) => {
    const start = logicalGroupDragRef.current;
    if (!start) return;
    const next = applyDynamicHandleAnchors(moveActionLogicalGroup(nodesRef.current, start, delta),
      graphConnections, {}, handleConnectionIndex, [...start.keys()]);
    nodesRef.current = next;
    next.forEach((node) => {
      if (start.has(node.id)) storedPositionsRef.current[node.id] = node.position;
    });
    setNodes(next);
  }, [graphConnections, handleConnectionIndex, setNodes]);

  const handleGroupDrag = useCallback((delta) => {
    if (!canManageProject || previewMode || isEditorOpen) return;
    moveGroupFromStart(delta);
  }, [canManageProject, previewMode, isEditorOpen, moveGroupFromStart]);

  const handleGroupDragEnd = useCallback((cancelled = false) => {
    if (!logicalGroupDragRef.current) return;
    if (cancelled) moveGroupFromStart({ x: 0, y: 0 });
    logicalGroupDragRef.current = null;
    isNodeDraggingRef.current = false;
    setIsNodeDragging(false);
    if (!cancelled) persistPositions(Object.fromEntries(nodesRef.current.map((node) => [node.id, node.position])));
  }, [moveGroupFromStart, persistPositions]);

  const handleGroupNudge = useCallback((group, delta) => {
    if (!handleGroupDragStart(group)) return;
    handleGroupDrag(delta);
    handleGroupDragEnd();
  }, [handleGroupDragStart, handleGroupDrag, handleGroupDragEnd]);

  useEffect(() => {
    if (previewMode || !savePreviewLayout.current || nodes.some((node) => node.data?.node?.assistantPreview)) return;
    savePreviewLayout.current = false;
    persistPositions(Object.fromEntries(nodes.map((node) => [node.id, node.position])));
  }, [nodes, persistPositions, previewMode]);

  const getNodeDropTargetAtPosition = useCallback((node) => {
    if (!canManageProject || isEditorOpen || previewMode) return null;
    node = constrainProjectDagDropNode(node, reactFlowInstance?.getNode?.('center'), NODE_WIDTH);
    if (node?.data?.node?.kind === 'preparation') {
      const sourceKey = node.data.node.sourceNodeKey;
      if (actionControls?.preparationPendingId || ['queued', 'running', 'processing'].includes(nodeRuntime?.[sourceKey]?.status)) return null;
      return getProjectDagPreparationDropTarget(node, reactFlowInstance?.getNodes?.() || [], graphConnections,
        reactFlowInstance?.getIntersectingNodes?.(node, true) || [], { zoom: reactFlowInstance?.getZoom?.() || 1 });
    }
    if (node?.data?.node?.nodeType !== 'action') return null;
    const edgeDropTarget = getProjectDagActionEdgeDropTarget(
      node,
      reactFlowInstance?.getNodes?.() || [],
      graphConnections,
      { zoom: reactFlowInstance?.getZoom?.() || 1 }
    );
    if (edgeDropTarget) return edgeDropTarget;

    const intersectingNodes = (
      typeof reactFlowInstance?.getIntersectingNodes === 'function'
    )
      ? reactFlowInstance.getIntersectingNodes(node, true)
      : [];
    return getProjectDagActionDropTarget(
      node,
      Array.isArray(intersectingNodes) ? intersectingNodes : []
    );
  }, [canManageProject, graphConnections, reactFlowInstance, isEditorOpen, previewMode, actionControls?.preparationPendingId, nodeRuntime]);

  const updateDragPreview = useCallback((node) => {
    const dropTarget = getNodeDropTargetAtPosition(node);
    const targetKey = dropTarget?.targetNode?.key || null;
    const targetEdgeId = dropTarget?.targetEdgeId || null;
    setDraggedActionDropTargetKey((currentTargetKey) => (
      currentTargetKey === targetKey ? currentTargetKey : targetKey
    ));
    setDraggedActionDropTargetEdgeId((currentTargetEdgeId) => (
      currentTargetEdgeId === targetEdgeId ? currentTargetEdgeId : targetEdgeId
    ));
  }, [getNodeDropTargetAtPosition]);

  const dragPreviewCallbackRef = useRef(updateDragPreview);
  useEffect(() => {
    dragPreviewCallbackRef.current = updateDragPreview;
  }, [updateDragPreview]);
  const dragPreviewScheduler = useMemo(
    () => createDagFrameScheduler((node) => dragPreviewCallbackRef.current(node)), []
  );
  useEffect(() => () => dragPreviewScheduler.cancel(), [caseid, dragPreviewScheduler]);

  const handleNodeDragStart = useCallback((_event, node) => {
    dragPreviewScheduler.cancel();
    layoutSaveRevisionRef.current += 1;
    storedPositionsRef.current = { ...storedPositionsRef.current,
      ...Object.fromEntries((reactFlowInstance?.getNodes?.() || []).map((current) => [current.id, current.position])) };
    isNodeDraggingRef.current = true;
    setIsNodeDragging(true);
    setDraggedActionDropTargetKey(null);
    setDraggedActionDropTargetEdgeId(null);
    const draggingAction = node?.data?.node?.nodeType === 'action' || node?.data?.node?.kind === 'preparation';
    setIsDraggingAction(draggingAction);
  }, [dragPreviewScheduler, reactFlowInstance]);

  const handleNodeDrag = useCallback((_event, node) => {
    if (!canManageProject || !['action', 'preparation'].includes(node?.data?.node?.nodeType)) return;
    dragPreviewScheduler.schedule(node);
  }, [canManageProject, dragPreviewScheduler]);

  const handleNodeDragStop = useCallback((_event, node) => {
    // Mouse-up always performs an exact hit test, never a delayed preview.
    dragPreviewScheduler.cancel();
    isNodeDraggingRef.current = false;
    setIsNodeDragging(false);
    setDraggedActionDropTargetKey(null);
    setDraggedActionDropTargetEdgeId(null);
    setIsDraggingAction(false);
    if (!node?.id || !node.position) {
      return;
    }
    const dropNodes = constrainProjectDagSections(
      (reactFlowInstance?.getNodes?.() || []).map((current) => current.id === node.id ? node : current),
      NODE_WIDTH, { movingCenter: node.id === 'center' }
    );
    const dropPositions = Object.fromEntries(
      dropNodes
        .filter((currentNode) => graphNodeKeys.has(currentNode.id))
        .map((currentNode) => [currentNode.id, currentNode.position])
    );

    const dropTarget = getNodeDropTargetAtPosition(node);
    if (dropTarget?.preparationNode && typeof actionControls?.onPreparationCommand === 'function') {
      persistPositions(dropPositions);
      setPendingActionMove({ ...dropTarget, nodeId: node.id, preparationItem: dropTarget.preparationNode.item });
      return;
    }
    if (
      dropTarget
      && typeof actionControls?.onMoveToFlow === 'function'
      && dropTarget.actionNode?.item?.id !== undefined
    ) {
      // Persist the physical mouse-up position before asking about the
      // separate logical flow/order move.
      persistPositions(dropPositions);
      setPendingActionMove({
        ...dropTarget,
        nodeId: node.id,
        actionId: dropTarget.actionNode.item.id,
      });
      return;
    }

    persistPositions(dropPositions);
  }, [
    actionControls, dragPreviewScheduler, getNodeDropTargetAtPosition, graphNodeKeys,
    persistPositions, reactFlowInstance,
  ]);

  const handleCancelActionMove = useCallback(() => {
    // Cancel only the logical flow/order move. The canvas position was
    // already persisted at mouse-up and must remain where the user dropped it.
    confirmedActionDropRef.current = null;
    setPendingActionMove(null);
  }, []);

  const handleConfirmActionMove = useCallback(() => {
    if (!pendingActionMove || !canManageProject || isEditorOpen || previewMode) return;
    if (pendingActionMove.preparationItem) {
      const move = pendingActionMove;
      setPendingActionMove(null);
      actionControls?.onPreparationCommand?.(move.preparationItem, 'move', { index: move.targetIndex });
      return;
    }
    const { actionId, targetFlowId } = pendingActionMove;
    confirmedActionDropRef.current = { nodeId: pendingActionMove.nodeId };
    setPendingActionMove(null);

    // The canvas position was persisted at drag-stop. This confirmation only
    // applies the separate logical flow/order move and preserves that position.
    actionControls?.onMoveToFlow?.(actionId, targetFlowId, {
      targetNode: pendingActionMove.targetNode,
      insertBefore: Boolean(pendingActionMove.targetEdgeId),
    });
  }, [actionControls, pendingActionMove, canManageProject, isEditorOpen, previewMode]);

  useEffect(() => {
    if (
      !canManageProject
      || !caseid
      || loading
      || layoutLoading
      || inputLoading
      || outputLoading
      || pendingActionMove
      || isNodeDragging
      || nodes.length === 0
    ) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const currentPositions = Object.fromEntries(
        nodes
          .filter((node) => graphNodeKeys.has(node.id))
          .map((node) => [node.id, node.position])
      );
      const snapshot = buildProjectDagLayoutSnapshot(
        graphNodeSpecs,
        currentPositions,
        graphConnections
      );
      if (buildProjectDagLayoutSignature(snapshot) !== lastPersistedLayoutSignatureRef.current) {
        persistPositions(currentPositions);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    canManageProject, caseid, graphConnections, graphNodeKeys, graphNodeSpecs,
    inputLoading, isNodeDragging, layoutLoading, loading, nodes, outputLoading, pendingActionMove,
    persistPositions,
  ]);

  const canResetLayout = canManageProject && caseid != null && !previewMode;
  const resetDisabled = helpSelectionActive || isEditorOpen || loading || inputLoading || outputLoading
    || layoutLoading || isNodeDragging || Boolean(pendingActionMove);
  const handleReset = useCallback(() => {
    if (!canResetLayout || resetDisabled || isNodeDraggingRef.current) return;
    setIsResetConfirmOpen(true);
  }, [canResetLayout, resetDisabled]);

  useEffect(() => setLayoutReset(false), [caseid]);

  const handleConfirmReset = useCallback(() => {
    if (!canResetLayout || resetDisabled || isNodeDraggingRef.current) return;
    setIsResetConfirmOpen(false);
    setCollapsedFlowIds(new Set());
    const arranged = applyDynamicHandleAnchors(
      constrainProjectDagSections(alignProjectDagFlowRows(nodesRef.current.map((node) => ({
        ...node,
        hidden: false,
        position: defaultPositions[node.id] || node.position,
      })), stagedFlowBranches, NODE_WIDTH, flowLabelHeights), NODE_WIDTH),
      graphConnections
    );
    nodesRef.current = arranged;
    setNodes(arranged);
    persistPositions(Object.fromEntries(arranged.map((node) => [node.id, node.position])));
    setLayoutReset(true);
    requestAnimationFrame(() => reactFlowInstance?.fitView({ padding: 0.1, duration: reduceMotion ? 0 : 320, maxZoom: 1.1 }));
  }, [canResetLayout, resetDisabled, defaultPositions, flowLabelHeights, graphConnections,
    persistPositions, reactFlowInstance, reduceMotion, setNodes, stagedFlowBranches]);

  const handleCancelReset = useCallback(() => setIsResetConfirmOpen(false), []);

  const handleNodeClick = useCallback((_event, reactFlowNode) => {
    if (reactFlowNode.data.node.kind === 'center') {
      canvasActionControls.onOpenDashboard?.();
      return;
    }
    onNodeActivate(reactFlowNode.data.node);
  }, [canvasActionControls, onNodeActivate]);

  const updateDraftTutorialAnchor = useCallback(() => {
    if (!showDraftTutorial || !isDraftTutorialOpen || typeof window === 'undefined') {
      setDraftTutorialAnchor(null);
      return;
    }

    const container = canvasContainerRef.current;
    const flowElement = container?.querySelector('.react-flow');
    const target = draftTutorialStep === 'save'
      ? document.querySelector('[data-draft-tutorial-target="save"]')
      : container?.querySelector(
        `[data-tutorial-target="${draftTutorialStep === 'action' ? 'action' : 'connector'}"]`
      );
    if (!flowElement || !target) {
      setDraftTutorialAnchor(null);
      return;
    }

    const flowRect = flowElement.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const targetX = targetRect.left - flowRect.left + (targetRect.width / 2);
    const targetY = targetRect.top - flowRect.top + (targetRect.height / 2);
    const estimatedBubbleWidth = flowRect.width < 560 ? 280 : 340;
    if (draftTutorialStep === 'save') {
      const panelLeft = Math.min(
        Math.max(estimatedBubbleWidth / 2 + 16, targetX),
        Math.max(estimatedBubbleWidth / 2 + 16, flowRect.width - estimatedBubbleWidth / 2 - 16)
      );
      const tailLeft = Math.min(
        Math.max(24, targetX - (panelLeft - estimatedBubbleWidth / 2) - 8),
        estimatedBubbleWidth - 40
      );
      setDraftTutorialAnchor({
        left: panelLeft,
        top: 16,
        side: 'top',
        tailLeft,
      });
      return;
    }
    const showOnLeft = targetX > estimatedBubbleWidth + 34;

    setDraftTutorialAnchor({
      left: showOnLeft ? targetX - 18 : targetX + targetRect.width + 18,
      top: targetY,
      side: showOnLeft ? 'right' : 'left',
    });
  }, [draftTutorialStep, isDraftTutorialOpen, showDraftTutorial]);

  useEffect(() => {
    let frame;
    const scheduleAnchorUpdate = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateDraftTutorialAnchor);
    };

    scheduleAnchorUpdate();
    window.addEventListener('resize', scheduleAnchorUpdate);
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleAnchorUpdate)
      : null;
    if (resizeObserver && canvasContainerRef.current) resizeObserver.observe(canvasContainerRef.current);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleAnchorUpdate);
      resizeObserver?.disconnect();
    };
  }, [updateDraftTutorialAnchor]);

  const handleDraftTutorialMove = useCallback(() => {
    if (!showDraftTutorial || !isDraftTutorialOpen) return;
    requestAnimationFrame(updateDraftTutorialAnchor);
  }, [isDraftTutorialOpen, showDraftTutorial, updateDraftTutorialAnchor]);

  const draftTutorialStepNumber = {
    connector: 1,
    action: 2,
    save: 3,
  }[draftTutorialStep] || 1;
  const draftTutorialStepTitle = draftTutorialStep === 'connector'
    ? t('dag.draft_tutorial_connector_title', 'Add your data')
    : draftTutorialStep === 'action'
      ? t('dag.draft_tutorial_action_title', 'Choose what happens next')
      : t('dag.draft_tutorial_save_title', 'Finish your project');
  const draftTutorialStepBody = draftTutorialStep === 'connector'
    ? t(
      'dag.draft_tutorial_connector_body',
      'Add a connector with the highlighted + on the left to choose where your data comes from.'
    )
    : draftTutorialStep === 'action'
      ? t(
        'dag.draft_tutorial_action_body',
        'Add your first action with the highlighted + on Main Flow to choose what happens next.'
      )
      : t(
        'dag.draft_tutorial_save_body',
        'Give your project a name above, then select Save & open DAG. Your choices will be saved automatically.'
      );

  const logicalGroupConnections = useMemo(() => [
    ...flowBranches.flatMap(buildFlowStageConnections), ...buildFlowActionConnections(flowBranches, t),
  ], [flowBranches, t]);
  const logicalGroups = useMemo(() => buildActionLogicalGroupBounds(nodes, 'action', flowBranches, flowLabelHeights,
    logicalGroupConnections), [nodes, flowBranches, flowLabelHeights, logicalGroupConnections]);
  const logicalGroupTokenSummaries = useMemo(() => buildLogicalGroupTokenSummaries(flowBranches, logicalGroupConnections),
    [flowBranches, logicalGroupConnections]);
  const visibleCollapsedGroups = useMemo(() => {
    if (previewMode) return new Set();
    if (!normalizedDagSearchQuery) return collapsedLogicalGroupIds;
    return new Set(logicalGroups.filter((group) => collapsedLogicalGroupIds.has(group.id)
      && !group.nodeKeys.some((key) => dagSearchMatchKeys.has(key))).map((group) => group.id));
  }, [previewMode, normalizedDagSearchQuery, collapsedLogicalGroupIds, logicalGroups, dagSearchMatchKeys]);
  const displayedGraph = useMemo(() => reconnectCollapsedGroupHandles(
    buildCollapsedLogicalGroupView(nodes, edges, logicalGroups, visibleCollapsedGroups)),
    [nodes, edges, logicalGroups, visibleCollapsedGroups]);

  return (
    <Box ref={canvasContainerRef} data-testid="project-dag-canvas" sx={{
      position: 'relative',
      width: '100%',
      height: 'var(--project-dag-height, clamp(460px, calc(100vh - 240px), 720px))',
      minHeight: { xs: 'var(--project-dag-min-height, 460px)', md: 'var(--project-dag-min-height, 520px)' },
      boxSizing: 'border-box', pt: '56px',
      overflow: 'hidden',
      borderRadius: 3,
      border: '1px solid rgba(87,87,176,.14)',
      backgroundColor: '#fbfbff',
      // Keep search/reset and empty-state panels outside the movable graph.
      // React Flow applies its protected overflow as an inline style.
      '& .react-flow': { overflow: 'visible !important' },
      '& .react-flow__renderer': { overflow: 'hidden' },
      '& .react-flow__panel.top': { transform: 'translateY(-56px)' },
      ...(reduceEffects ? DAG_REDUCED_EFFECT_STYLES : {}),
      '& .react-flow__edges': { zIndex: 0 },
      '& .react-flow__edgelabel-renderer': { zIndex: 1 },
      '& .react-flow__nodes': { zIndex: 2 },
      '& .react-flow__viewport-portal': { zIndex: -1, pointerEvents: 'none' },
      '@media (prefers-reduced-motion: reduce)': {
        ...DAG_REDUCED_MOTION_STYLES,
      },
      }}>
      {layoutLoading && <CircularProgress size={24} sx={{ position: 'absolute', top: 20, right: 20, zIndex: 12 }} />}
      <DagInteractionContext.Provider value={isDraggingAction}>
        <DagNodeInternalsContext.Provider value={nodeInternalsBatcher}>
        <DagEntranceContext.Provider value={seenEntranceNodeIds}>
        <DagEffectsContext.Provider value={reduceEffects}>
        <DagBuildFocusContext.Provider value={pageVisible ? buildFocusKey : null}>
        <ReactFlow
          nodes={displayedGraph.nodes}
          edges={displayedGraph.edges}
          onlyRenderVisibleElements={largeDag}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onNodeClick={handleNodeClick}
          onNodeDragStart={handleNodeDragStart}
          onNodeDrag={handleNodeDrag}
          onNodeDragStop={handleNodeDragStop}
          onMove={handleDraftTutorialMove}
          onInit={setReactFlowInstance}
          deleteKeyCode={null}
          nodesDraggable={!isEditorOpen && !pendingActionMove}
          nodesConnectable={false}
          elementsSelectable
          panOnDrag={!isEditorOpen && !pendingActionMove}
          zoomOnScroll={!isEditorOpen && !pendingActionMove}
          zoomOnPinch={!isEditorOpen && !pendingActionMove}
          zoomOnDoubleClick={!isEditorOpen && !pendingActionMove}
          minZoom={0.25}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          colorMode="light"
          ariaLabelConfig={{
            'node.a11yDescription.default': t('dag.node_keyboard_hint', 'Press Enter to select this node. Use arrow keys to move it.'),
          }}
        >
        <ProjectDagSections centerNodeWidth={PROJECT_WORKSPACE_NODE_WIDTH} t={t}
          onAddConnector={canManageProject && canvasActionControls.canManage && !isEditorOpen && !previewMode
            ? canvasActionControls.onAddConnector : undefined}
          onAddFlow={canManageProject && canvasActionControls.canManage && !isEditorOpen && !previewMode
            ? canvasActionControls.onAddFlow : undefined} />
        <ActionFlowLogicalGroupFrames
          key={caseid} nodes={nodes} groups={logicalGroups}
          tokenSummaries={logicalGroupTokenSummaries} locale={locale}
          collapsedIds={visibleCollapsedGroups}
          onToggleCollapse={!previewMode ? toggleLogicalGroupCollapse : undefined}
          canMove={canManageProject && !previewMode && !isEditorOpen && !loading && !inputLoading && !outputLoading
            && !layoutLoading && !pendingActionMove && !helpSelectionActive
            && (!isNodeDragging || Boolean(logicalGroupDragRef.current))}
          onGroupDragStart={handleGroupDragStart} onGroupDrag={handleGroupDrag} onGroupDragEnd={handleGroupDragEnd}
          getZoom={() => reactFlowInstance?.getZoom() || 1}
          onGroupNudge={handleGroupNudge}
          canEdit={Boolean(actionControls?.canManage)}
          onRenameGroup={actionControls?.onRenameLogicalGroup}
          onEditGroup={actionControls?.onEditLogicalGroup}
          onSetGroupEnabled={actionControls?.onSetLogicalGroupEnabled}
          onRunGroup={actionControls?.onRunLogicalGroup}
          pendingRunGroupId={actionControls?.pendingRunGroupId}
          pendingGroupId={actionControls?.pendingLogicalGroupId}
          t={t}
        />
        <ActionFlowLogicalGroupFrames nodes={nodes} nodeType="preparation" t={t} />
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.2} color="rgba(87,87,176,.2)" />
        <Controls position="bottom-left" showInteractive={false} />
        <MiniMap
          position="bottom-right"
          pannable
          zoomable
          nodeColor={getMiniMapNodeColor}
          nodeStrokeWidth={2}
          maskColor="rgba(245,245,255,.72)"
          style={MINI_MAP_STYLE}
        />
        {showDraftTutorial && isDraftTutorialOpen && (
          <Panel
            position="top-left"
            style={{
              margin: 0,
              zIndex: 10,
              left: draftTutorialAnchor?.left ?? 16,
              top: draftTutorialAnchor?.top ?? 16,
              transform: draftTutorialAnchor
                ? draftTutorialAnchor.side === 'right'
                  ? 'translate(-100%, -50%)'
                  : draftTutorialAnchor.side === 'left'
                    ? 'translate(0, -50%)'
                    : 'translate(-50%, 0)'
                : 'none',
            }}
          >
            <Box
              role="dialog"
              aria-label={t('dag.draft_tutorial_title', 'Start building your project')}
              sx={{
                position: 'relative',
                width: { xs: 280, sm: 340 },
                p: { xs: 2, sm: 2.25 },
                border: '1px solid rgba(87, 87, 176, 0.22)',
                borderRadius: 3,
                background: 'linear-gradient(145deg, rgba(255,255,255,.98), rgba(246,247,255,.98))',
                boxShadow: '0 12px 30px rgba(44, 46, 120, 0.16)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: 16,
                  height: 16,
                  background: '#f8f8ff',
                    ...(draftTutorialAnchor?.side === 'top'
                      ? {
                        left: draftTutorialAnchor.tailLeft,
                        top: -9,
                        transform: 'rotate(45deg)',
                        borderLeft: '1px solid rgba(87, 87, 176, 0.22)',
                        borderTop: '1px solid rgba(87, 87, 176, 0.22)',
                      }
                      : draftTutorialAnchor?.side === 'right'
                    ? {
                      right: -9,
                      top: '50%',
                      transform: 'translateY(-50%) rotate(-45deg)',
                      borderTop: '1px solid rgba(87, 87, 176, 0.22)',
                      borderRight: '1px solid rgba(87, 87, 176, 0.22)',
                    }
                    : draftTutorialAnchor?.side === 'left'
                      ? {
                        left: -9,
                        top: '50%',
                        transform: 'translateY(-50%) rotate(45deg)',
                        borderLeft: '1px solid rgba(87, 87, 176, 0.22)',
                        borderBottom: '1px solid rgba(87, 87, 176, 0.22)',
                      }
                      : {
                        left: 24,
                        bottom: -9,
                        transform: 'rotate(45deg)',
                        borderRight: '1px solid rgba(87, 87, 176, 0.22)',
                        borderBottom: '1px solid rgba(87, 87, 176, 0.22)',
                      }),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 3 }}>
                <TipsAndUpdatesRoundedIcon sx={{ color: '#5757b0', mt: 0.1 }} />
                <Box>
                  <Typography variant="overline" sx={{ display: 'block', color: '#7777c8', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1.2 }}>
                    {t('dag.draft_tutorial_progress', 'Step {{step}} of 3', { step: draftTutorialStepNumber })}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: '#30306a', fontWeight: 800, lineHeight: 1.25, mt: 0.25 }}>
                    {t('dag.draft_tutorial_title', 'Start building your project')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#69697d', mt: 0.35, lineHeight: 1.45 }}>
                    {t('dag.draft_tutorial_body', 'Follow three simple steps to create your first automation.')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1.75, mb: 1.5 }} aria-hidden="true">
                {[1, 2, 3].map((step) => (
                  <Box
                    key={step}
                    sx={{
                      flex: 1,
                      height: 4,
                      borderRadius: 99,
                      backgroundColor: step <= draftTutorialStepNumber ? '#5757b0' : 'rgba(87, 87, 176, 0.14)',
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.25, borderRadius: 2, backgroundColor: 'rgba(87, 87, 176, 0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', backgroundColor: '#5757b0', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                  {draftTutorialStepNumber}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#3d3d78', fontWeight: 800, lineHeight: 1.25 }}>
                    {draftTutorialStepTitle}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#4c4c65', mt: 0.4, lineHeight: 1.5 }}>
                    {draftTutorialStepBody}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', color: '#777789', mt: 1.25, lineHeight: 1.4 }}>
                {draftTutorialStep === 'save'
                  ? t('dag.draft_tutorial_save_hint', 'You can change these choices later in your project.')
                  : t('dag.draft_tutorial_next_hint', 'You can skip this guide and explore freely.')}
              </Typography>
              <Button
                size="small"
                onClick={dismissDraftTutorial}
                sx={{ mt: 1.1, px: 0, minWidth: 0, color: '#5757b0', textTransform: 'none', fontWeight: 700 }}
              >
                {t('dag.draft_tutorial_dismiss', 'Skip tutorial')}
              </Button>
              <IconButton
                aria-label={t('dag.draft_tutorial_close', 'Close tutorial')}
                size="small"
                onClick={dismissDraftTutorial}
                sx={{ position: 'absolute', top: 7, right: 7, color: '#777789' }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Panel>
        )}
        <Panel position="top-right" style={{ maxWidth: 'calc(100% - 24px)' }}>
          <Box
            className="nodrag nopan"
            onPointerDown={(event) => event.stopPropagation()}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.75 }}>
              {(searchableDagNodes.length > 0 || actionSearchQuery) && (
                <TextField
                  disabled={helpSelectionActive}
                  size="small"
                  value={actionSearchQuery}
                  onChange={(event) => setActionSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setActionSearchQuery('');
                  }}
                  placeholder={t('dag.search_nodes', 'Search flows, inputs, and actions')}
                  inputProps={{ 'aria-label': t('dag.search_nodes', 'Search flows, inputs, and actions') }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                    endAdornment: actionSearchQuery ? (
                      <InputAdornment position="end" sx={{ ml: 0.25 }}>
                        <Typography
                          component="span"
                          variant="caption"
                          aria-live="polite"
                          sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                        >
                          {dagSearchMatchCount}/{searchableDagNodes.length}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={t('template.clear_search', 'Clear Search')}
                          onClick={() => setActionSearchQuery('')}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: 'clamp(160px, 28vw, 280px)',
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    },
                  }}
                />
              )}
              {actionControls?.canManage && actionControls?.onSuggestTracking && !previewMode && <Tooltip
                title={t('actionFunnel.activateTrackingHelp')} describeChild arrow>
                <Button size="small" variant="outlined" startIcon={<AutoAwesomeRoundedIcon />}
                  disabled={helpSelectionActive || isEditorOpen || loading || isNodeDragging || Boolean(pendingActionMove)}
                  onClick={actionControls.onSuggestTracking}
                  sx={{ textTransform: 'none', bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                  {t('actionFunnel.activateTracking')}
                </Button>
              </Tooltip>}
              {caseid != null && actionControls?.onOpenHistory && !previewMode && <Button
                size="small" variant="outlined" startIcon={<HistoryIcon />}
                disabled={helpSelectionActive || isEditorOpen || loading || Boolean(pendingActionMove)}
                onClick={() => layoutSaveQueueRef.current.then(() => actionControls.onOpenHistory())}
                sx={{ textTransform: 'none', backgroundColor: '#fff', whiteSpace: 'nowrap' }}>
                {t('dag.history.title')}
              </Button>}
              {canResetLayout && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleReset}
                  disabled={resetDisabled}
                  sx={{ textTransform: 'none', backgroundColor: '#fff', whiteSpace: 'nowrap' }}
                >
                  {t('workflow.canvas_reset', 'Reset')}
                </Button>
              )}
              {!previewMode && <DagHelp canvasRef={canvasContainerRef} reactFlowInstance={reactFlowInstance}
                nodes={displayedGraph.nodes} caseid={caseid} locale={locale} onActiveChange={setHelpSelectionActive}
                boxCreation={actionControls?.boxCreation}
                onUngroupLogicalGroups={actionControls?.canManage ? actionControls.onUngroupLogicalGroups : undefined}
                onCreateLogicalGroup={actionControls?.canManage ? actionControls.onCreateLogicalGroup : undefined}
                onPrepareCorrection={actionControls?.canManage ? actionControls.onPrepareCorrection : undefined}
                closeOnDisable={isEditorOpen}
                disabled={isEditorOpen || loading || isNodeDragging || Boolean(pendingActionMove)} t={t} />}
            </Box>
            {layoutError && (
              <Typography
                role="alert"
                variant="caption"
                sx={{ color: 'error.main', backgroundColor: 'rgba(255,255,255,.92)', px: 0.75, borderRadius: 1 }}
              >
                {layoutError}
              </Typography>
            )}
            {layoutReset && !layoutError && <Typography role="status" variant="caption"
              sx={{ color: 'success.main', backgroundColor: 'rgba(255,255,255,.92)', px: 0.75, borderRadius: 1 }}>
              {t('dag.layout_reset', 'Layout reset.')}
            </Typography>}
          </Box>
        </Panel>
        {!inputLoading && inputNodes.length === 0 && caseid != null && (
          <Panel position="top-left">
            <Typography color="text.secondary" variant="body2">{t('dag.no_inputs', 'No configured data connectors')}</Typography>
          </Panel>
        )}
        {!outputLoading && (flowBranches.length === 0 || outputError) && (
          <Panel position="top-center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                color={outputError ? 'error.main' : 'text.secondary'}
                variant="body2"
                role={outputError ? 'alert' : undefined}
              >
                {outputError
                  ? t('dag.outputs_load_error', 'Output flows could not be loaded.')
                  : t('dag.no_outputs', 'No output flows')}
              </Typography>
              {outputError && typeof actionControls?.onRetryOutputs === 'function' && (
                <Button
                  className="nodrag nopan"
                  size="small"
                  variant="outlined"
                  onClick={actionControls.onRetryOutputs}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {t('dag.retry_outputs', 'Try again')}
                </Button>
              )}
            </Box>
          </Panel>
        )}
        <Dialog
          open={isResetConfirmOpen}
          onClose={handleCancelReset}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('workflow.canvas_reset_confirm_title', 'Reset the canvas layout?')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t(
                'workflow.canvas_reset_confirm_message',
                'This replaces your custom layout with the default: sources, preparation steps, your workspace, flows and actions return to their default positions, and collapsed flows open. No actions or data are deleted.'
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCancelReset}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" color="warning" onClick={handleConfirmReset} disabled={!canResetLayout || resetDisabled}>
              {t('workflow.canvas_reset_confirm_action', 'Reset layout')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={Boolean(pendingActionMove)}
          onClose={handleCancelActionMove}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {pendingActionMove?.preparationItem ? t('connector_setup.step.move_title')
              : t('dag.move_action_confirm_title', 'Confirm action move')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" fontWeight="bold" paragraph>
              {pendingActionMove?.preparationItem ? t('connector_setup.step.move_question', {
                name: pendingActionMove.preparationNode.label, position: pendingActionMove.targetIndex + 1,
              }) : t('dag.move_action_confirm_question', 'Move “{{action}}” to “{{flow}}”?', {
                action: pendingActionMove?.actionNode?.label || t('dag.unnamed_action', 'Unnamed action'),
                flow: pendingActionMove?.targetNode?.nodeType === 'flow'
                  ? pendingActionMove?.targetNode?.label
                  : flowBranches.find((branch) => (
                    String(branch?.flow?.id) === String(pendingActionMove?.targetFlowId)
                  ))?.flow?.name
                    || pendingActionMove?.targetNode?.label
                    || t('dag.unnamed_flow', 'Unnamed output flow'),
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingActionMove?.preparationItem ? t('connector_setup.step.move_help') : t(
                'dag.move_action_confirm_warning',
                'The action will be inserted at the selected position, and following actions will move down.'
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCancelActionMove}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmActionMove}
            >
              {pendingActionMove?.preparationItem ? t('connector_setup.step.move_confirm')
                : t('dag.move_action_confirm', 'Move action')}
            </Button>
          </DialogActions>
        </Dialog>
        </ReactFlow>
        </DagBuildFocusContext.Provider>
        </DagEffectsContext.Provider>
        </DagEntranceContext.Provider>
        </DagNodeInternalsContext.Provider>
      </DagInteractionContext.Provider>
    </Box>
  );
};

export default UnfoldingCanvas;
